import { Head, router, Link } from '@inertiajs/react'
import AppShell from '@/components/AppShell'
import AgendaItemDescriptionEditor from '@/components/AgendaItemDescriptionEditor'
import { type FormEvent, useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Settings,
  Image,
  ImagePlus,
  Award,
  Mic2,
  CalendarDays,
  MapPin,
  Handshake,
  ExternalLink as ExternalLinkIcon,
  Share2,
  ClipboardList,
  FileText,
  Type,
  LayoutTemplate,
  Mail,
  Sliders,
  ArrowLeft,
  Save,
  Palette,
  Video,
  Globe,
  Plus,
  Trash2,
  Loader2,
  Menu,
  X,
  Paintbrush,
  EyeOff,
  GitBranch,
  Link2,
  ChevronDown,
  Copy,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventData {
  id: number
  name: string
  eventDate: string | null
  eventTime: string | null
  videoLink: string | null
  about: string | null
  location: string | null
  primaryColor: string
  secondaryColor: string
  eventUserTypes: string
  ended: boolean
  active: boolean
  hasAgenda: boolean
  hasSpeakers: boolean
  hasPartners: boolean
  hasExternalLinks: boolean
  hasSocialLinks: boolean
  checkInPolicy: string
  font: string | null
  customFontName: string | null
  template: number | null
  registerationMessage: string | null
  pendingRegistrationMessage: string | null
  pendingRegistrationMessage_en: string
  pendingRegistrationMessage_ar: string
  emailSubject: string | null
  emailMessage: string | null
  pendingEmailSubject: string | null
  pendingEmailSubject_en: string
  pendingEmailSubject_ar: string
  pendingEmailMessage_en: string
  pendingEmailMessage_ar: string
  rejectedEmailSubject: string | null
  rejectedEmailSubject_en: string
  rejectedEmailSubject_ar: string
  rejectedEmailMessage_en: string
  rejectedEmailMessage_ar: string
  sendQr: boolean
  printQr: boolean
  badgeWidth: string | null
  badgeHeight: string | null
  badgeTextPosition: number | null
  badgeNameSize: number | null
  badgeSecondarySize: number | null
  badgeMarginLeft: number | null
  badgeMarginRight: number | null
  badgeTextAlignment: string
  badgeDisplayFields: string[][]
  physicalBadgePrint?: boolean
  physicalPrintQr?: boolean | null
  physicalBadgeWidth?: string | null
  physicalBadgeHeight?: string | null
  physicalBadgeTextPosition?: number | null
  physicalBadgeNameSize?: number | null
  physicalBadgeSecondarySize?: number | null
  physicalBadgeMarginLeft?: number | null
  physicalBadgeMarginRight?: number | null
  physicalBadgeTextAlignment?: string
  physicalBadgeDisplayFields?: string[][]
  seatRows: number | null
  seatColumns: number | null
  logoUrl: string | null
  aboutBackgroundUrl: string | null
  emailImageUrl: string | null
  registrationBannerUrl: string | null
  registrationMobileBannerUrl: string | null
  registrationFooterImageUrl: string | null
  registrationDesktopDesignUrl: string | null
  registrationMobileDesignUrl: string | null
  eventImageUrls: Array<{ id: string; url: string }>
  regBgColor: string
  regTextColor: string
  regCardBgColor: string
  regButtonColor: string
  regButtonTextColor: string
  regInputBorderColor: string
  regFormWidth: string
  regFormAlignment: string
  regMobileFormAlignment: string
  regShowLangSwitcher: boolean
  regCustomCss: string
  regHeaderHtml: string
  regHeaderHtml_en: string
  regHeaderHtml_ar: string
  regFooterHtml: string
  regFooterHtml_en: string
  regFooterHtml_ar: string
  eventTerms: string
  eventTerms_en: string
  eventTerms_ar: string
  sendRegistrationEmail: boolean
  emailMessage_en: string
  emailMessage_ar: string
  senderName: string
  senderEmail: string
  mailFromDomain?: string
  settings: EventSettingsData
  emailWhitelistCount: number
}

interface CompanionConfig {
  role: string
  user_type: string
  name_field: string
  gender_field?: string
}

interface EntityMappingRule {
  values: string[]
  user_type: string | null
}

interface EventSettingsData {
  registration: {
    auto_approve: boolean
    require_email_whitelist: boolean
    max_tickets_per_email: number | null
    post_confirmation_params: Record<string, string>
    use_inertia: boolean
    sync_google_sheet: boolean
    multi_ticket_field?: string | null
    multi_step_enabled?: boolean
    agenda_selection_required?: boolean
    agenda_layout?: 'list' | 'cards' | 'timeline' | 'table'
    agenda_group_sessions?: boolean
    agenda_show_from_to_labels?: boolean
    header_placement?: 'inside' | 'outside'
    footer_placement?: 'inside' | 'outside'
    title_field_type?: 'text' | 'select'
    position_field_type?: 'text' | 'select'
    position_options?: string[]
    show_privacy_policy?: boolean
    seating_map_enabled?: boolean
  }
  companion_tickets: {
    enabled: boolean
    primary_user_type: string
    companions: CompanionConfig[]
    inherit_fields: string[]
    copy_to_custom_fields: Record<string, string>
  }
  entity_mapping: {
    enabled: boolean
    field: string
    entity_field?: string
    rules: EntityMappingRule[]
    default_user_type: string | null
    guest_entity_value: string
  }
  validations: Array<Record<string, unknown>>
  capacity_rules: Array<Record<string, unknown>>
  emails: {
    use_legacy_template: string | null
    locale_routing: Record<string, unknown> | null
    approval_template: string
    attach_ticket_pdf?: boolean
    include_ticket_links?: boolean
  }
  badge_styles: Record<string, { css_class?: string; accent?: string; surface?: string }>
}

interface SpeakerItem {
  id: number
  name: string
  description: string | null
  imageUrl: string | null
  deleteUrl: string
}

interface PartnerItem {
  id: number
  name: string
  imageUrl: string | null
  deleteUrl: string
}

interface ExternalLinkItem {
  id: number
  name: string
  link: string
  deleteUrl: string
}

interface SocialLinkData {
  id: number | null
  twitter: string | null
  instagram: string | null
  linkedin: string | null
  facebook: string | null
  youtube: string | null
  updateUrl: string | null
}

interface VenueData {
  id: number
  name: string | null
  location: string | null
  updateUrl: string
  deleteUrl: string
}

interface DayItemData {
  id: number
  description: string
  descriptionEn?: string
  descriptionAr?: string
  time: string
  endTime: string
  capacity?: number | null
  groupLabel?: string | null
  groupLabelEn?: string | null
  groupLabelAr?: string | null
  registrationUrl?: string
}

interface DayData {
  id: number
  number: number
  date?: string | null
  dayItems: DayItemData[]
  updateUrl: string
  deleteUrl: string
}

interface AgendaData {
  id: number
  numberOfDays: number | null
  days: DayData[]
  updateUrl: string
  deleteUrl: string
}

interface EventFileItem {
  id: number
  fileName: string | null
  deleteUrl: string
}

interface BadgeItem {
  id: number
  name: string
  isDefault: boolean
  badgeBackgroundUrl: string | null
}

interface FieldCondition {
  id: string
  sourceField: string
  sourceFieldId?: number | null
  sourceType: 'standard' | 'custom'
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty' | 'is_one_of'
  value: string
  action: 'show' | 'hide' | 'require' | 'unrequire'
}

interface RegistrationFieldItem {
  field: string
  order: number
  active: boolean
  custom: boolean
  width: number
  required: boolean
  uniquePerEvent: boolean
  startNewRow?: boolean
  hidden: boolean
  conditions: FieldCondition[]
}

interface CustomFieldItem {
  id: number
  name: string
  name_en: string
  name_ar: string
  fieldType: string
  required: boolean
  order: number
  width: number
  uniquePerEvent: boolean
  startNewRow?: boolean
  showChoiceStats?: boolean
  fieldOptions: string
  fieldOptions_en: string
  fieldOptions_ar: string
  optionIconUrls?: Array<string | null>
  optionIconBlobIds?: Array<string | null>
  hidden: boolean
  conditions: FieldCondition[]
}

interface RegistrationLinkItem {
  id: number
  slug: string
  label: string
  userType: string
  allFields: boolean
  fieldKeys: string[]
  allSessions: boolean
  sessionIds: number[]
  soldOutSessionIds: number[]
  active: boolean
  registrationUrl: string
  useCustomEmail: boolean
  emailSubjectEn: string
  emailSubjectAr: string
  emailBodyEn: string
  emailBodyAr: string
  attachTicketPdf: boolean | null
  includeTicketLinks: boolean | null
  includeSaveTheDate: boolean
  requireApproval: boolean
  emailAttachments: Array<{ id: number; filename: string; url: string }>
  agendaPdf?: { filename: string; url: string } | null
  removeAgendaPdf?: boolean
  removeAttachmentIds?: number[]
  pendingAttachmentNames?: string[]
}

interface RegistrationLinkOptions {
  fields: Array<{ key: string; label: string; custom: boolean }>
  sessions: Array<{ id: number; label: string }>
}

interface SetupProps {
  event: EventData
  activeSection: string
  showRestricted: boolean
  fonts: string[]
  speakers: SpeakerItem[]
  partners: PartnerItem[]
  externalLinks: ExternalLinkItem[]
  socialLink: SocialLinkData | null
  venue: VenueData | null
  agenda: AgendaData | null
  eventFiles: EventFileItem[]
  badges: BadgeItem[]
  registrationFields: RegistrationFieldItem[]
  customFields: CustomFieldItem[]
  registrationLinks: RegistrationLinkItem[]
  registrationLinkOptions: RegistrationLinkOptions
  badgeAvailableFields: Array<{ field: string; label: string; isCustom: boolean }>
  locale: string
  logoUrl: string | null
  accountName: string
  userEmail: string
  canEditAccount: boolean
  urls: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    dashboard: string
    updateEvent: string
    setup: string
    deleteEventImageAttachment: string
    createSpeaker: string
    createPartner: string
    createExternalLink: string
    createEventFile: string
    createSocialLink: string
    createVenue: string
    createAgenda: string
    createDay: string
    createDayItem: string
    whatsappTemplates?: string
    invitations?: string
  }
  translations: Record<string, string>
}

/* ------------------------------------------------------------------ */
/*  Sidebar nav section definition                                     */
/* ------------------------------------------------------------------ */

interface NavItem {
  key: string
  param: string
  icon: typeof Settings
  labelKey: string
  condition?: (props: SetupProps) => boolean
}

const NAV_ITEMS: NavItem[] = [
  { key: 'event', param: '', icon: Settings, labelKey: 'mainSettings' },
  {
    key: 'event_images',
    param: 'event_images=true',
    icon: Image,
    labelKey: 'eventImages',
    condition: (p) => p.showRestricted,
  },
  { key: 'badge', param: 'badge=true', icon: Award, labelKey: 'badge' },
  {
    key: 'speaker',
    param: 'speaker=true',
    icon: Mic2,
    labelKey: 'speakers',
    condition: (p) => p.showRestricted && p.event.hasSpeakers,
  },
  {
    key: 'agenda',
    param: 'agenda=true',
    icon: CalendarDays,
    labelKey: 'schedule',
    condition: (p) => p.showRestricted && p.event.hasAgenda,
  },
  {
    key: 'venue',
    param: 'venue=true',
    icon: MapPin,
    labelKey: 'venue',
    condition: (p) => p.showRestricted,
  },
  {
    key: 'partner',
    param: 'partner=true',
    icon: Handshake,
    labelKey: 'partners',
    condition: (p) => p.showRestricted && p.event.hasPartners,
  },
  {
    key: 'external_link',
    param: 'external_link=true',
    icon: ExternalLinkIcon,
    labelKey: 'externalLinks',
    condition: (p) => p.showRestricted && p.event.hasExternalLinks,
  },
  {
    key: 'social_link',
    param: 'social_link=true',
    icon: Share2,
    labelKey: 'socialLinks',
    condition: (p) => p.showRestricted && p.event.hasSocialLinks,
  },
  { key: 'registration', param: 'registration=true', icon: ClipboardList, labelKey: 'registration' },
  { key: 'registration_rules', param: 'registration_rules=true', icon: GitBranch, labelKey: 'registrationRules' },
  { key: 'registration_links', param: 'registration_links=true', icon: Link2, labelKey: 'registrationLinks' },
  { key: 'registration_design', param: 'registration_design=true', icon: Paintbrush, labelKey: 'registrationDesign' },
  { key: 'event_file', param: 'event_file=true', icon: FileText, labelKey: 'eventFiles' },
  { key: 'font', param: 'font=true', icon: Type, labelKey: 'font' },
  {
    key: 'template',
    param: 'template=true',
    icon: LayoutTemplate,
    labelKey: 'template',
    condition: (p) => p.showRestricted,
  },
  { key: 'messages', param: 'messages=true', icon: Mail, labelKey: 'messages' },
  {
    key: 'options',
    param: 'options=true',
    icon: Sliders,
    labelKey: 'options',
    condition: (p) => p.showRestricted,
  },
]

/* ------------------------------------------------------------------ */
/*  Helper: form submit                                                */
/* ------------------------------------------------------------------ */

function submitSetupForm(
  e: FormEvent<HTMLFormElement>,
  updateUrl: string,
  kind: string,
  onFinish?: () => void,
) {
  e.preventDefault()
  const fd = new FormData(e.currentTarget)
  fd.append('kind', kind)

  // Strip empty file inputs (browser adds 0-byte File objects)
  const emptyKeys: string[] = []
  fd.forEach((v, k) => {
    if (v instanceof File && v.size === 0) emptyKeys.push(k)
  })
  emptyKeys.forEach((k) => fd.delete(k))

  router.put(updateUrl, fd as any, {
    preserveScroll: true,
    forceFormData: true,
    onFinish,
  })
}

/* ------------------------------------------------------------------ */
/*  Reusable form components                                           */
/* ------------------------------------------------------------------ */

function FormField({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#37352F]">{label}</Label>
      {hint && <p className="text-xs text-[#91918E]">{hint}</p>}
      {children}
    </div>
  )
}

function formInput(className?: string) {
  return `h-9 rounded-md border border-[#E8E8E5] bg-white px-3 text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors ${className || ''}`
}

function SaveButton({ t, saving }: { t: Record<string, string>; saving: boolean }) {
  return (
    <Button
      type="submit"
      disabled={saving}
      className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white gap-1.5"
    >
      {saving ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Save className="h-3.5 w-3.5" />
      )}
      {saving ? t.saving : t.save}
    </Button>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Main Settings                                             */
/* ------------------------------------------------------------------ */

function MainSettingsSection({ event, urls, t, showRestricted }: { event: EventData; urls: SetupProps['urls']; t: Record<string, string>; showRestricted: boolean }) {
  const [ended, setEnded] = useState(event.ended)
  const [saving, setSaving] = useState(false)

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'event', () => setSaving(false))
      }}
      className="space-y-6"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField label={t.eventName}>
          <Input name="event[name]" defaultValue={event.name || ''} className={formInput()} required />
        </FormField>

        <FormField label={t.eventDate}>
          <Input name="event[event_date]" type="date" defaultValue={event.eventDate || ''} className={formInput()} required />
        </FormField>

        <FormField label={t.eventTime}>
          <Input name="event[event_time]" type="time" defaultValue={event.eventTime || ''} className={formInput()} />
        </FormField>

        {showRestricted && (
          <FormField label={t.videoLink}>
            <Input name="event[video_link]" defaultValue={event.videoLink || ''} className={formInput()} placeholder="https://youtube.com/..." />
          </FormField>
        )}
      </div>

      <FormField label={t.aboutEvent}>
        <textarea
          name="event[about]"
          defaultValue={event.about || ''}
          rows={3}
          className={`w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y`}
          required
        />
      </FormField>

      <FormField label={t.eventLocation}>
        <Input name="event[location]" defaultValue={event.location || ''} className={formInput()} required />
      </FormField>

      <FormField label={t.logo}>
        <div className="flex items-center gap-3">
          {event.logoUrl && (
            <img src={event.logoUrl} alt="Logo" className="h-12 w-12 rounded-md border border-[#E8E8E5] object-contain" />
          )}
          <Input name="event[logo]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <div className="grid gap-5 sm:grid-cols-2">
        <FormField label={t.primaryColor}>
          <div className="flex items-center gap-2">
            <input
              name="event[primary_color]"
              type="color"
              defaultValue={event.primaryColor || '#000000'}
              className="h-9 w-12 cursor-pointer rounded border border-[#E8E8E5]"
            />
            <span className="text-xs text-[#91918E] font-mono">{event.primaryColor}</span>
          </div>
        </FormField>

        <FormField label={t.secondaryColor}>
          <div className="flex items-center gap-2">
            <input
              name="event[secondary_color]"
              type="color"
              defaultValue={event.secondaryColor || '#000000'}
              className="h-9 w-12 cursor-pointer rounded border border-[#E8E8E5]"
            />
            <span className="text-xs text-[#91918E] font-mono">{event.secondaryColor}</span>
          </div>
        </FormField>
      </div>

      <FormField label={t.userTypes} hint={t.userTypesHint}>
        <textarea
          name="event[event_user_types]"
          defaultValue={event.eventUserTypes || ''}
          rows={3}
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>

      <div className="flex items-center gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
        <input type="hidden" name="event[ended]" value={ended ? '1' : '0'} />
        <Switch checked={ended} onCheckedChange={setEnded} />
        <Label className="text-sm text-[#37352F] cursor-pointer">{t.ended}</Label>
      </div>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Options                                                   */
/* ------------------------------------------------------------------ */

function OptionsSection({ event, urls, t }: { event: EventData; urls: SetupProps['urls']; t: Record<string, string> }) {
  const [hasAgenda, setHasAgenda] = useState(event.hasAgenda)
  const [hasSpeakers, setHasSpeakers] = useState(event.hasSpeakers)
  const [hasPartners, setHasPartners] = useState(event.hasPartners)
  const [hasExternalLinks, setHasExternalLinks] = useState(event.hasExternalLinks)
  const [hasSocialLinks, setHasSocialLinks] = useState(event.hasSocialLinks)
  const [checkInPolicy, setCheckInPolicy] = useState(event.checkInPolicy || 'once')
  const [saving, setSaving] = useState(false)

  const toggles = [
    { name: 'has_agenda', label: t.hasAgenda, value: hasAgenda, set: setHasAgenda },
    { name: 'has_speakers', label: t.hasSpeakers, value: hasSpeakers, set: setHasSpeakers },
    { name: 'has_partners', label: t.hasPartners, value: hasPartners, set: setHasPartners },
    { name: 'has_external_links', label: t.hasExternalLinks, value: hasExternalLinks, set: setHasExternalLinks },
    { name: 'has_social_links', label: t.hasSocialLinks, value: hasSocialLinks, set: setHasSocialLinks },
  ]

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'options', () => setSaving(false))
      }}
      className="space-y-4"
    >
      <FormField label={t.checkInPolicy}>
        <select
          name="event[check_in_policy]"
          value={checkInPolicy}
          onChange={(e) => setCheckInPolicy(e.target.value)}
          className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
        >
          <option value="allow_duplicate">{t.checkInPolicyAllowDuplicate}</option>
          <option value="allow_when_available">{t.checkInPolicyAllowWhenAvailable}</option>
          <option value="once">{t.checkInPolicyOnce}</option>
          <option value="once_per_day">{t.checkInPolicyOncePerDay}</option>
        </select>
      </FormField>

      <div className="space-y-1">
        {toggles.map((toggle) => (
          <div key={toggle.name} className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
            <input type="hidden" name={`event[${toggle.name}]`} value={toggle.value ? '1' : '0'} />
            <Label className="text-sm text-[#37352F] cursor-pointer">{toggle.label}</Label>
            <Switch checked={toggle.value} onCheckedChange={toggle.set} />
          </div>
        ))}
      </div>
      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Registration Links (per user type)                        */
/* ------------------------------------------------------------------ */

function RegistrationLinksSection({
  links: initialLinks,
  options,
  urls,
  t,
}: {
  links: RegistrationLinkItem[]
  options: RegistrationLinkOptions
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [links, setLinks] = useState(initialLinks)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLinks(initialLinks)
  }, [initialLinks])

  function updateLink(index: number, patch: Partial<RegistrationLinkItem>) {
    setLinks((prev) => prev.map((link, i) => (i === index ? { ...link, ...patch } : link)))
  }

  function toggleField(index: number, fieldKey: string) {
    const link = links[index]
    const next = link.fieldKeys.includes(fieldKey)
      ? link.fieldKeys.filter((key) => key !== fieldKey)
      : [...link.fieldKeys, fieldKey]
    updateLink(index, { allFields: false, fieldKeys: next })
  }

  function toggleSession(index: number, sessionId: number) {
    const link = links[index]
    const offering = !link.sessionIds.includes(sessionId)
    const next = offering
      ? [...link.sessionIds, sessionId]
      : link.sessionIds.filter((id) => id !== sessionId)
    const soldOutSessionIds = offering
      ? (link.soldOutSessionIds || [])
      : (link.soldOutSessionIds || []).filter((id) => id !== sessionId)
    updateLink(index, { allSessions: false, sessionIds: next, soldOutSessionIds })
  }

  function toggleSoldOutSession(index: number, sessionId: number) {
    const link = links[index]
    const current = link.soldOutSessionIds || []
    const next = current.includes(sessionId)
      ? current.filter((id) => id !== sessionId)
      : [...current, sessionId]
    updateLink(index, { soldOutSessionIds: next })
  }

  function markAttachmentForRemoval(index: number, attachmentId: number) {
    const link = links[index]
    updateLink(index, {
      removeAttachmentIds: [...(link.removeAttachmentIds || []), attachmentId],
      emailAttachments: link.emailAttachments.filter((attachment) => attachment.id !== attachmentId),
    })
  }

  function triStateValue(value: boolean | null | undefined) {
    if (value === true) return 'yes'
    if (value === false) return 'no'
    return 'default'
  }

  function parseTriState(value: string): boolean | null {
    if (value === 'yes') return true
    if (value === 'no') return false
    return null
  }

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'registration_links', () => setSaving(false))
      }}
      className="space-y-6"
    >
      <input type="hidden" name="registration_links" value={JSON.stringify(links)} />

      <div className="space-y-3">
        <div>
          <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.registrationLinks || 'Registration Links'}</h3>
          <p className="text-xs text-[#91918E] mt-1">{t.registrationLinksHint || 'Each user type gets a unique registration URL with its own fields and sessions.'}</p>
        </div>

        {links.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#E8E8E5] px-4 py-8 text-center text-sm text-[#91918E]">
            {t.noRegistrationLinks || 'Add user types in Main Settings first, then return here.'}
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link, index) => (
              <div key={link.id || link.userType} className="rounded-lg border border-[#E8E8E5] p-4 space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-[#37352F]">{link.label || link.userType}</div>
                    <div className="text-xs text-[#91918E] mt-0.5">{t.userType || 'User type'}: {link.userType}</div>
                  </div>
                  <CopyRegistrationLinkButton url={link.registrationUrl} t={t} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField label={t.linkSlug || 'URL slug'}>
                    <Input
                      value={link.slug}
                      onChange={(e) => updateLink(index, { slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      className={formInput()}
                    />
                  </FormField>
                  <FormField label={t.linkLabel || 'Label'}>
                    <Input value={link.label} onChange={(e) => updateLink(index, { label: e.target.value })} className={formInput()} />
                  </FormField>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="text-sm text-[#37352F]">{t.registrationFieldsLabel || 'Registration fields'}</Label>
                    <label className="flex items-center gap-2 text-xs text-[#91918E]">
                      <input
                        type="checkbox"
                        checked={link.allFields}
                        onChange={(e) => updateLink(index, { allFields: e.target.checked, fieldKeys: e.target.checked ? [] : link.fieldKeys })}
                        className="h-3.5 w-3.5 rounded border-[#E8E8E5]"
                      />
                      {t.allFields || 'All fields'}
                    </label>
                  </div>
                  {!link.allFields && (
                    <div className="flex flex-wrap gap-2">
                      {options.fields.map((field) => (
                        <label key={field.key} className="inline-flex items-center gap-1.5 rounded-md border border-[#E8E8E5] px-2 py-1 text-xs text-[#37352F]">
                          <input
                            type="checkbox"
                            checked={link.fieldKeys.includes(field.key)}
                            onChange={() => toggleField(index, field.key)}
                            className="h-3.5 w-3.5 rounded border-[#E8E8E5]"
                          />
                          {field.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="text-sm text-[#37352F]">{t.sessions || 'Sessions'}</Label>
                    <label className="flex items-center gap-2 text-xs text-[#91918E]">
                      <input
                        type="checkbox"
                        checked={link.allSessions}
                        onChange={(e) => updateLink(index, { allSessions: e.target.checked, sessionIds: e.target.checked ? [] : link.sessionIds })}
                        className="h-3.5 w-3.5 rounded border-[#E8E8E5]"
                      />
                      {t.allSessions || 'All sessions'}
                    </label>
                  </div>
                  <p className="text-xs text-[#91918E]">{t.soldOutSessionsHint || 'Mark sessions as fully booked for this link. They stay visible but cannot be selected.'}</p>
                  {options.sessions.length === 0 ? (
                    <p className="text-xs text-[#91918E]">{t.noSessionsConfigured || 'No agenda sessions configured yet.'}</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1 rounded-md border border-[#E8E8E5] p-2">
                      {options.sessions.map((session) => {
                        const offered = link.allSessions || link.sessionIds.includes(session.id)
                        const soldOut = (link.soldOutSessionIds || []).includes(session.id)
                        return (
                          <div key={session.id} className="flex flex-wrap items-start justify-between gap-2 text-xs text-[#37352F]">
                            {!link.allSessions ? (
                              <label className="flex items-start gap-2 min-w-0 flex-1">
                                <input
                                  type="checkbox"
                                  checked={link.sessionIds.includes(session.id)}
                                  onChange={() => toggleSession(index, session.id)}
                                  className="mt-0.5 h-3.5 w-3.5 rounded border-[#E8E8E5]"
                                />
                                <span className="min-w-0">{session.label}</span>
                              </label>
                            ) : (
                              <span className="min-w-0 flex-1 pt-0.5">{session.label}</span>
                            )}
                            <label className={`inline-flex items-center gap-1.5 shrink-0 ${offered ? 'text-[#EB5757]' : 'text-[#C4C4C0]'}`}>
                              <input
                                type="checkbox"
                                checked={soldOut}
                                disabled={!offered}
                                onChange={() => toggleSoldOutSession(index, session.id)}
                                className="h-3.5 w-3.5 rounded border-[#E8E8E5]"
                              />
                              {t.soldOut || 'Sold out'}
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div>
                    <Label className="text-sm text-[#37352F]">{t.agendaPdf || 'Agenda PDF'}</Label>
                    <p className="text-xs text-[#91918E] mt-0.5">{t.agendaPdfHint || 'Shown on the confirmation page and attached with the ticket email for this user type.'}</p>
                  </div>
                  {link.agendaPdf && !link.removeAgendaPdf && (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-[#E8E8E5] px-3 py-2 text-xs">
                      <a href={link.agendaPdf.url} target="_blank" rel="noreferrer" className="text-[#2383E2] truncate">{link.agendaPdf.filename}</a>
                      <button
                        type="button"
                        onClick={() => updateLink(index, { removeAgendaPdf: true, agendaPdf: null })}
                        className="text-[#EB5757] hover:text-[#D04040]"
                      >
                        {t.remove || 'Remove'}
                      </button>
                    </div>
                  )}
                  {link.id ? (
                    <Input
                      type="file"
                      accept="application/pdf,.pdf"
                      name={`registration_link_agenda_pdf[${link.id}]`}
                      className={formInput()}
                    />
                  ) : (
                    <p className="text-xs text-[#91918E]">{t.saveLinkBeforeAttachments || 'Save once before uploading attachments.'}</p>
                  )}
                </div>

                <div className="rounded-md border border-[#E8E8E5] p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label className="text-sm text-[#37352F]">{t.typeEmailSettings || 'Email settings'}</Label>
                      <p className="text-xs text-[#91918E] mt-0.5">{t.typeEmailSettingsHint || 'Optional per-type email content, attachments, and calendar invite.'}</p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-[#91918E]">
                      <input
                        type="checkbox"
                        checked={link.useCustomEmail}
                        onChange={(e) => updateLink(index, { useCustomEmail: e.target.checked })}
                        className="h-3.5 w-3.5 rounded border-[#E8E8E5]"
                      />
                      {t.customEmailForType || 'Custom email body'}
                    </label>
                  </div>

                  {link.useCustomEmail && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormField label={`${t.emailSubject || 'Email subject'} (EN)`}>
                        <Input
                          value={link.emailSubjectEn}
                          onChange={(e) => updateLink(index, { emailSubjectEn: e.target.value })}
                          className={formInput()}
                        />
                      </FormField>
                      <FormField label={`${t.emailSubject || 'Email subject'} (AR)`}>
                        <Input
                          value={link.emailSubjectAr}
                          onChange={(e) => updateLink(index, { emailSubjectAr: e.target.value })}
                          className={formInput()}
                          dir="rtl"
                        />
                      </FormField>
                      <FormField label={`${t.emailMessage || 'Email message'} (EN)`} wrapClass="md:col-span-2">
                        <textarea
                          value={link.emailBodyEn}
                          onChange={(e) => updateLink(index, { emailBodyEn: e.target.value })}
                          rows={4}
                          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
                        />
                      </FormField>
                      <FormField label={`${t.emailMessage || 'Email message'} (AR)`} wrapClass="md:col-span-2">
                        <textarea
                          value={link.emailBodyAr}
                          onChange={(e) => updateLink(index, { emailBodyAr: e.target.value })}
                          rows={4}
                          dir="rtl"
                          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
                        />
                      </FormField>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FormField label={t.attachTicketPdf || 'Attach ticket PDF'}>
                      <select
                        value={triStateValue(link.attachTicketPdf)}
                        onChange={(e) => updateLink(index, { attachTicketPdf: parseTriState(e.target.value) })}
                        className={formInput()}
                      >
                        <option value="default">{t.useEventDefault || 'Use event default'}</option>
                        <option value="yes">{t.yes || 'Yes'}</option>
                        <option value="no">{t.no || 'No'}</option>
                      </select>
                    </FormField>
                    <FormField label={t.includeTicketLinks || 'Include ticket links'}>
                      <select
                        value={triStateValue(link.includeTicketLinks)}
                        onChange={(e) => updateLink(index, { includeTicketLinks: parseTriState(e.target.value) })}
                        className={formInput()}
                      >
                        <option value="default">{t.useEventDefault || 'Use event default'}</option>
                        <option value="yes">{t.yes || 'Yes'}</option>
                        <option value="no">{t.no || 'No'}</option>
                      </select>
                    </FormField>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-[#37352F] pb-2">
                        <input
                          type="checkbox"
                          checked={link.includeSaveTheDate}
                          onChange={(e) => updateLink(index, { includeSaveTheDate: e.target.checked })}
                          className="h-4 w-4 rounded border-[#E8E8E5]"
                        />
                        {t.includeSaveTheDate || 'Attach save-the-date calendar'}
                      </label>
                    </div>
                    <div className="flex items-end md:col-span-3">
                      <label className="flex items-center gap-2 text-sm text-[#37352F] pb-2">
                        <input
                          type="checkbox"
                          checked={Boolean(link.requireApproval)}
                          onChange={(e) => updateLink(index, { requireApproval: e.target.checked })}
                          className="h-4 w-4 rounded border-[#E8E8E5]"
                        />
                        {t.requireApproval || 'Require approval for this registration link'}
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-[#37352F]">{t.emailAttachments || 'Email attachments'}</Label>
                    {link.emailAttachments.length > 0 && (
                      <div className="space-y-1">
                        {link.emailAttachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between gap-2 rounded-md border border-[#E8E8E5] px-3 py-2 text-xs">
                            <a href={attachment.url} target="_blank" rel="noreferrer" className="text-[#2383E2] truncate">{attachment.filename}</a>
                            <button
                              type="button"
                              onClick={() => markAttachmentForRemoval(index, attachment.id)}
                              className="text-[#EB5757] hover:text-[#D04040]"
                            >
                              {t.remove || 'Remove'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {link.id ? (
                      <Input
                        type="file"
                        multiple
                        name={`registration_link_attachments[${link.id}][]`}
                        className={formInput()}
                      />
                    ) : (
                      <p className="text-xs text-[#91918E]">{t.saveLinkBeforeAttachments || 'Save once before uploading attachments.'}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Registration Rules                                        */
/* ------------------------------------------------------------------ */

function RegistrationRulesSection({
  event,
  urls,
  t,
}: {
  event: EventData
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [settings, setSettings] = useState<EventSettingsData>(() => ({
    ...event.settings,
    entity_mapping: {
      field: 'Entity',
      rules: [],
      default_user_type: null,
      guest_entity_value: 'ضيف',
      enabled: false,
      ...(event.settings?.entity_mapping || {}),
      rules: event.settings?.entity_mapping?.rules ?? [],
    },
  }))
  const [saving, setSaving] = useState(false)
  const [positionOptionsText, setPositionOptionsText] = useState(
    () => (event.settings?.registration?.position_options || []).join('\n'),
  )

  function updateRegistration(key: keyof EventSettingsData['registration'], value: unknown) {
    setSettings((prev) => ({
      ...prev,
      registration: { ...prev.registration, [key]: value },
    }))
  }

  function updateCompanion(key: keyof EventSettingsData['companion_tickets'], value: unknown) {
    setSettings((prev) => ({
      ...prev,
      companion_tickets: { ...prev.companion_tickets, [key]: value },
    }))
  }

  function updateEntity(key: keyof EventSettingsData['entity_mapping'], value: unknown) {
    setSettings((prev) => ({
      ...prev,
      entity_mapping: { ...prev.entity_mapping, [key]: value },
    }))
  }

  function updateCompanionRow(index: number, patch: Partial<CompanionConfig>) {
    setSettings((prev) => {
      const companions = [...prev.companion_tickets.companions]
      companions[index] = { ...companions[index], ...patch }
      return {
        ...prev,
        companion_tickets: { ...prev.companion_tickets, companions },
      }
    })
  }

  function addCompanionRow() {
    setSettings((prev) => ({
      ...prev,
      companion_tickets: {
        ...prev.companion_tickets,
        companions: [
          ...prev.companion_tickets.companions,
          { role: `guest_${prev.companion_tickets.companions.length + 1}`, user_type: 'Guest', name_field: '', gender_field: '' },
        ],
      },
    }))
  }

  function removeCompanionRow(index: number) {
    setSettings((prev) => ({
      ...prev,
      companion_tickets: {
        ...prev.companion_tickets,
        companions: prev.companion_tickets.companions.filter((_, i) => i !== index),
      },
    }))
  }

  function updateEntityRule(index: number, patch: Partial<EntityMappingRule>) {
    setSettings((prev) => {
      const rules = [...(prev.entity_mapping.rules || [])]
      rules[index] = { ...rules[index], ...patch }
      return {
        ...prev,
        entity_mapping: { ...prev.entity_mapping, rules },
      }
    })
  }

  function updateEntityRuleValues(index: number, raw: string) {
    const values = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    updateEntityRule(index, { values })
  }

  function addEntityRule() {
    setSettings((prev) => ({
      ...prev,
      entity_mapping: {
        ...prev.entity_mapping,
        rules: [...(prev.entity_mapping.rules || []), { values: [], user_type: '' }],
      },
    }))
  }

  function removeEntityRule(index: number) {
    setSettings((prev) => ({
      ...prev,
      entity_mapping: {
        ...prev.entity_mapping,
        rules: (prev.entity_mapping.rules || []).filter((_, i) => i !== index),
      },
    }))
  }

  const userTypeOptions = (event.eventUserTypes || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'registration_rules', () => setSaving(false))
      }}
      className="space-y-6"
    >
      <input type="hidden" name="event[settings]" value={JSON.stringify(settings)} />

      <div className="space-y-3">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.registrationRules}</h3>
        <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
          <Label className="text-sm text-[#37352F]">{t.autoApproveRegistrations}</Label>
          <Switch checked={settings.registration.auto_approve} onCheckedChange={(v) => updateRegistration('auto_approve', v)} />
        </div>
        <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
          <div>
            <Label className="text-sm text-[#37352F]">{t.requireEmailWhitelist}</Label>
            <p className="text-xs text-[#91918E]">{t.emailWhitelistHint}</p>
          </div>
          <Switch checked={settings.registration.require_email_whitelist} onCheckedChange={(v) => updateRegistration('require_email_whitelist', v)} />
        </div>
        <FormField label={t.maxTicketsPerEmail}>
          <Input
            type="number"
            min={0}
            value={settings.registration.max_tickets_per_email ?? ''}
            onChange={(e) => updateRegistration('max_tickets_per_email', e.target.value ? Number(e.target.value) : null)}
            className={formInput()}
            placeholder="No limit"
          />
        </FormField>
        <FormField
          label={t.titleFieldType || 'Title field type'}
          hint={t.titleFieldTypeHint || 'When dropdown is enabled, title options come from the localized titles list.'}
        >
          <select
            value={settings.registration.title_field_type || 'text'}
            onChange={(e) => updateRegistration('title_field_type', e.target.value as EventSettingsData['registration']['title_field_type'])}
            className={formInput()}
          >
            <option value="text">{t.titleFieldTypeText || 'Free text'}</option>
            <option value="select">{t.titleFieldTypeSelect || 'Dropdown'}</option>
          </select>
        </FormField>
        <FormField
          label={t.positionFieldType || 'Position field type'}
          hint={t.positionFieldTypeHint || 'When dropdown is enabled, position options are used on registration and admin forms.'}
        >
          <select
            value={settings.registration.position_field_type || 'text'}
            onChange={(e) => updateRegistration('position_field_type', e.target.value as EventSettingsData['registration']['position_field_type'])}
            className={formInput()}
          >
            <option value="text">{t.positionFieldTypeText || 'Free text'}</option>
            <option value="select">{t.positionFieldTypeSelect || 'Dropdown'}</option>
          </select>
        </FormField>
        {(settings.registration.position_field_type === 'select' || settings.registration.seating_map_enabled) && (
          <FormField
            label={t.positionOptions || 'Position options'}
            hint={t.positionOptionsHint || 'One option per line. Used for dropdowns and live seating roles (1st = admin, 2nd = فني 1, 3rd = فني 2).'}
          >
            <textarea
              value={positionOptionsText}
              onChange={(e) => {
                const text = e.target.value
                setPositionOptionsText(text)
                updateRegistration(
                  'position_options',
                  text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
                )
              }}
              rows={4}
              className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
              placeholder={'إداري\nفني 1\nفني 2'}
            />
          </FormField>
        )}
        <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
          <div>
            <Label className="text-sm text-[#37352F]">{t.seatingMapEnabled || 'Live seating map'}</Label>
            <p className="text-xs text-[#91918E]">{t.seatingMapEnabledHint || 'Interactive table seating map for assigning guests during check-in and printing.'}</p>
          </div>
          <Switch checked={settings.registration.seating_map_enabled === true} onCheckedChange={(v) => updateRegistration('seating_map_enabled', v)} />
        </div>
        <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
          <div>
            <Label className="text-sm text-[#37352F]">{t.multiStepRegistration || 'Multi-step registration'}</Label>
            <p className="text-xs text-[#91918E]">{t.multiStepRegistrationHint || 'Landing → form → session selection → confirmation'}</p>
          </div>
          <Switch checked={settings.registration.multi_step_enabled === true} onCheckedChange={(v) => updateRegistration('multi_step_enabled', v)} />
        </div>
        {settings.registration.multi_step_enabled && event.hasAgenda && (
          <>
            <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
              <div>
                <Label className="text-sm text-[#37352F]">{t.requireSessionSelection || 'Require session selection'}</Label>
                <p className="text-xs text-[#91918E]">{t.requireSessionSelectionHint || 'Registrants must pick at least one agenda session'}</p>
              </div>
              <Switch checked={settings.registration.agenda_selection_required !== false} onCheckedChange={(v) => updateRegistration('agenda_selection_required', v)} />
            </div>
            <FormField
              label={t.sessionLayout || 'Session layout'}
              hint={t.sessionLayoutHint || 'How agenda sessions appear during registration.'}
            >
              <select
                value={settings.registration.agenda_layout || 'list'}
                onChange={(e) => updateRegistration('agenda_layout', e.target.value as EventSettingsData['registration']['agenda_layout'])}
                className={formInput()}
              >
                <option value="list">{t.sessionLayoutList || 'List'}</option>
                <option value="cards">{t.sessionLayoutCards || 'Cards'}</option>
                <option value="timeline">{t.sessionLayoutTimeline || 'Timeline'}</option>
                <option value="table">{t.sessionLayoutTable || 'Table'}</option>
              </select>
            </FormField>
            <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
              <div>
                <Label className="text-sm text-[#37352F]">{t.groupSessions || 'Group sessions'}</Label>
                <p className="text-xs text-[#91918E]">{t.groupSessionsHint || 'Show group headings on the session selection page. Turn off to list sessions without groups.'}</p>
              </div>
              <Switch checked={settings.registration.agenda_group_sessions !== false} onCheckedChange={(v) => updateRegistration('agenda_group_sessions', v)} />
            </div>
            <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
              <div>
                <Label className="text-sm text-[#37352F]">{t.showFromToLabels || 'Show From / To labels'}</Label>
                <p className="text-xs text-[#91918E]">{t.showFromToLabelsHint || 'Column titles on the table layout. Times stay visible either way.'}</p>
              </div>
              <Switch checked={settings.registration.agenda_show_from_to_labels !== false} onCheckedChange={(v) => updateRegistration('agenda_show_from_to_labels', v)} />
            </div>
          </>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.companionTickets}</h3>
            <p className="text-xs text-[#91918E] mt-1">{t.companionTicketsHint}</p>
          </div>
          <Switch checked={settings.companion_tickets.enabled} onCheckedChange={(v) => updateCompanion('enabled', v)} />
        </div>
        {settings.companion_tickets.enabled && (
          <div className="space-y-3 rounded-lg border border-[#E8E8E5] p-4">
            <FormField label={t.primaryUserType}>
              <Input
                value={settings.companion_tickets.primary_user_type}
                onChange={(e) => updateCompanion('primary_user_type', e.target.value)}
                className={formInput()}
              />
            </FormField>
            {settings.companion_tickets.companions.map((companion, index) => (
              <div key={`${companion.role}-${index}`} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end border border-[#F1F1EF] rounded-md p-3">
                <FormField label={t.companionRole}>
                  <Input value={companion.role} onChange={(e) => updateCompanionRow(index, { role: e.target.value })} className={formInput()} />
                </FormField>
                <FormField label={t.companionUserType}>
                  <Input value={companion.user_type} onChange={(e) => updateCompanionRow(index, { user_type: e.target.value })} className={formInput()} />
                </FormField>
                <FormField label={t.companionNameField}>
                  <Input value={companion.name_field} onChange={(e) => updateCompanionRow(index, { name_field: e.target.value })} className={formInput()} />
                </FormField>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <FormField label={t.companionGenderField}>
                      <Input value={companion.gender_field || ''} onChange={(e) => updateCompanionRow(index, { gender_field: e.target.value })} className={formInput()} />
                    </FormField>
                  </div>
                  <Button type="button" variant="outline" onClick={() => removeCompanionRow(index)} className="mb-0.5">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addCompanionRow}>
              <Plus className="h-4 w-4 mr-1" />
              {t.addCompanion}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.entityMapping}</h3>
            <p className="text-xs text-[#91918E] mt-1">{t.entityMappingHint}</p>
          </div>
          <Switch checked={settings.entity_mapping.enabled} onCheckedChange={(v) => updateEntity('enabled', v)} />
        </div>
        {settings.entity_mapping.enabled && (
          <div className="space-y-3 rounded-lg border border-[#E8E8E5] p-4">
            <FormField label={t.entityField}>
              <Input
                value={settings.entity_mapping.field || settings.entity_mapping.entity_field || ''}
                onChange={(e) => updateEntity('field', e.target.value)}
                className={formInput()}
                placeholder="Entity"
              />
            </FormField>

            {(settings.entity_mapping.rules || []).map((rule, index) => (
              <div key={`entity-rule-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-[#F1F1EF] rounded-md p-3">
                <FormField label={t.entityMappingValues}>
                  <textarea
                    value={(rule.values || []).join('\n')}
                    onChange={(e) => updateEntityRuleValues(index, e.target.value)}
                    rows={4}
                    className="flex w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
                    placeholder={'Graduate\nخريج'}
                  />
                </FormField>
                <div className="space-y-2">
                  <FormField label={t.entityMappingUserType}>
                    <Input
                      list={`entity-user-types-${index}`}
                      value={rule.user_type ?? ''}
                      onChange={(e) => updateEntityRule(index, { user_type: e.target.value || null })}
                      className={formInput()}
                      placeholder={t.entityMappingNoChange}
                    />
                    <datalist id={`entity-user-types-${index}`}>
                      {userTypeOptions.map((type) => (
                        <option key={type} value={type} />
                      ))}
                    </datalist>
                    <p className="text-xs text-[#91918E]">{t.entityMappingNoChange}</p>
                  </FormField>
                  <Button type="button" variant="outline" onClick={() => removeEntityRule(index)}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addEntityRule}>
              <Plus className="h-4 w-4 mr-1" />
              {t.addEntityMappingRule}
            </Button>

            <FormField label={t.entityMappingDefault}>
              <Input
                list="entity-user-types-default"
                value={settings.entity_mapping.default_user_type ?? ''}
                onChange={(e) => updateEntity('default_user_type', e.target.value || null)}
                className={formInput()}
                placeholder={t.entityMappingNoChange}
              />
              <datalist id="entity-user-types-default">
                {userTypeOptions.map((type) => (
                  <option key={type} value={type} />
                ))}
              </datalist>
            </FormField>

            <FormField label={t.guestEntityValue}>
              <Input
                value={settings.entity_mapping.guest_entity_value || ''}
                onChange={(e) => updateEntity('guest_entity_value', e.target.value)}
                className={formInput()}
              />
            </FormField>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-[#F1F1EF]">
        <label className="inline-flex items-center gap-2 text-sm text-[#37352F]">
          <input type="checkbox" name="event[apply_graduation_preset]" value="1" className="rounded border-[#E8E8E5]" />
          {t.applyGraduationPreset}
        </label>
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Messages                                                  */
/* ------------------------------------------------------------------ */

const PERSONALIZATION_TOKENS = [
  { token: '{{first_name}}', label: 'First Name' },
  { token: '{{last_name}}', label: 'Last Name' },
  { token: '{{full_name}}', label: 'Full Name' },
  { token: '{{email}}', label: 'Email' },
  { token: '{{phone}}', label: 'Phone' },
  { token: '{{organization}}', label: 'Organization' },
  { token: '{{position}}', label: 'Position' },
  { token: '{{user_type}}', label: 'User Type' },
  { token: '{{event_name}}', label: 'Event Name' },
  { token: '{{event_date}}', label: 'Event Date' },
  { token: '{{qr_link}}', label: 'QR Code Link' },
  { token: '{{badge_link}}', label: 'Badge Link' },
  { token: '{{registration_key}}', label: 'Registration Key' },
  { token: '{{tickets_html}}', label: 'Ticket Links Block' },
]

function MessagesSection({ event, urls, t }: { event: EventData; urls: SetupProps['urls']; t: Record<string, string> }) {
  const [sendQr, setSendQr] = useState(event.sendQr)
  const [sendRegistrationEmail, setSendRegistrationEmail] = useState(event.sendRegistrationEmail !== false)
  const [attachTicketPdf, setAttachTicketPdf] = useState(event.settings?.emails?.attach_ticket_pdf !== false)
  const [includeTicketLinks, setIncludeTicketLinks] = useState(event.settings?.emails?.include_ticket_links !== false)
  const [saving, setSaving] = useState(false)
  const [emailLocale, setEmailLocale] = useState<'en' | 'ar'>('en')
  const [sourceMode, setSourceMode] = useState(false)
  const [showTokenMenu, setShowTokenMenu] = useState(false)
  const tokenMenuRef = useRef<HTMLDivElement>(null)

  // Rich text editor refs (EN / AR)
  const editorEnRef = useRef<HTMLDivElement>(null)
  const editorArRef = useRef<HTMLDivElement>(null)
  const hiddenEnRef = useRef<HTMLInputElement>(null)
  const hiddenArRef = useRef<HTMLInputElement>(null)
  const sourceEnRef = useRef<HTMLTextAreaElement>(null)
  const sourceArRef = useRef<HTMLTextAreaElement>(null)

  // Set initial content once on mount
  useEffect(() => {
    if (editorEnRef.current) {
      editorEnRef.current.innerHTML = event.emailMessage_en || event.emailMessage || ''
    }
    if (editorArRef.current) {
      editorArRef.current.innerHTML = event.emailMessage_ar || event.emailMessage || ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close token menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (tokenMenuRef.current && !tokenMenuRef.current.contains(e.target as Node)) {
        setShowTokenMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Sync contentEditable → hidden input
  const syncEditorEn = () => {
    if (editorEnRef.current && hiddenEnRef.current) {
      hiddenEnRef.current.value = editorEnRef.current.innerHTML
    }
  }
  const syncEditorAr = () => {
    if (editorArRef.current && hiddenArRef.current) {
      hiddenArRef.current.value = editorArRef.current.innerHTML
    }
  }
  const activeEditorRef = emailLocale === 'en' ? editorEnRef : editorArRef
  const activeSyncEditor = emailLocale === 'en' ? syncEditorEn : syncEditorAr
  const activeSourceRef = emailLocale === 'en' ? sourceEnRef : sourceArRef
  const activeHiddenRef = emailLocale === 'en' ? hiddenEnRef : hiddenArRef

  // Save/restore selection so color picker doesn't lose it
  const savedSelectionRef = useRef<Range | null>(null)
  const saveSelection = () => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange()
    }
  }
  const restoreSelection = () => {
    const sel = window.getSelection()
    if (sel && savedSelectionRef.current) {
      sel.removeAllRanges()
      sel.addRange(savedSelectionRef.current)
    }
  }

  // execCommand wrapper
  const exec = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value)
    activeSyncEditor()
    activeEditorRef.current?.focus()
  }
  // Toggle source code view
  const toggleSourceMode = () => {
    if (sourceMode) {
      // Source → Visual: apply source textarea content to editor
      const src = activeSourceRef.current?.value || ''
      if (activeEditorRef.current) activeEditorRef.current.innerHTML = src
      if (activeHiddenRef.current) activeHiddenRef.current.value = src
    } else {
      // Visual → Source: copy editor innerHTML to textarea
      const html = activeEditorRef.current?.innerHTML || ''
      if (activeSourceRef.current) activeSourceRef.current.value = html
    }
    setSourceMode(!sourceMode)
  }

  // Sync source textarea → hidden input
  const syncSource = () => {
    const src = activeSourceRef.current?.value || ''
    if (activeHiddenRef.current) activeHiddenRef.current.value = src
  }

  // Insert personalization token at cursor
  const insertToken = (token: string) => {
    if (sourceMode) {
      const ta = activeSourceRef.current
      if (ta) {
        const start = ta.selectionStart
        const end = ta.selectionEnd
        const val = ta.value
        ta.value = val.substring(0, start) + token + val.substring(end)
        ta.selectionStart = ta.selectionEnd = start + token.length
        ta.focus()
        syncSource()
      }
    } else {
      activeEditorRef.current?.focus()
      document.execCommand('insertText', false, token)
      activeSyncEditor()
    }
    setShowTokenMenu(false)
  }

  return (
    <form
      onSubmit={(e) => {
        // Ensure hidden inputs are synced before submit
        syncEditorEn()
        syncEditorAr()
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'messages', () => setSaving(false))
      }}
      className="space-y-5"
    >
      <input
        type="hidden"
        name="event[settings]"
        value={JSON.stringify({
          emails: {
            attach_ticket_pdf: attachTicketPdf,
            include_ticket_links: includeTicketLinks,
          },
        })}
      />

      {/* Send Registration Email toggle */}
      <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
        <input type="hidden" name="event[send_registration_email]" value={sendRegistrationEmail ? '1' : '0'} />
        <div>
          <Label className="text-sm text-[#37352F] cursor-pointer">{t.sendRegistrationEmail || 'Send Registration Email'}</Label>
          <p className="text-xs text-[#91918E] mt-0.5">{t.sendRegistrationEmailHint || 'Automatically send a confirmation email when a user registers'}</p>
        </div>
        <Switch checked={sendRegistrationEmail} onCheckedChange={setSendRegistrationEmail} />
      </div>

      <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
        <div>
          <Label className="text-sm text-[#37352F] cursor-pointer">{t.attachTicketPdf || 'Attach ticket PDF'}</Label>
          <p className="text-xs text-[#91918E] mt-0.5">{t.attachTicketPdfHint || 'Attach ticket PDF files to the confirmation email'}</p>
        </div>
        <Switch checked={attachTicketPdf} onCheckedChange={setAttachTicketPdf} disabled={!sendRegistrationEmail} />
      </div>

      <div className="flex items-center justify-between rounded-md border border-[#E8E8E5] px-4 py-3">
        <div>
          <Label className="text-sm text-[#37352F] cursor-pointer">{t.includeTicketLinks || 'Include ticket links in email'}</Label>
          <p className="text-xs text-[#91918E] mt-0.5">{t.includeTicketLinksHint || 'Append ticket view links to the email body (also available via {{tickets_html}} token)'}</p>
        </div>
        <Switch checked={includeTicketLinks} onCheckedChange={setIncludeTicketLinks} disabled={!sendRegistrationEmail} />
      </div>

      {/* Sender Name & Email */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t.senderName || 'Sender Name'} hint={t.senderNameHint || 'The name that appears in the "From" field (default: Eventy)'}>
          <Input name="event[sender_name]" defaultValue={event.senderName || 'Eventy'} placeholder="Eventy" className={formInput()} />
        </FormField>
        <FormField label={t.senderEmail || 'Sender Email'} hint={t.senderEmailHint || `The email prefix before @${event.mailFromDomain || 'eventy.sa'} (default: noreply)`}>
          <div className="flex items-center gap-0">
            <Input name="event[sender_email]" defaultValue={event.senderEmail || 'noreply'} placeholder="noreply" className={formInput('rounded-r-none border-r-0')} />
            <span className="inline-flex h-9 items-center rounded-r-md border border-[#E8E8E5] bg-[#F7F7F5] px-3 text-sm text-[#91918E] select-none">@{event.mailFromDomain || 'eventy.sa'}</span>
          </div>
        </FormField>
      </div>

      <FormField label={t.registerationMessage} hint={t.registerationMessageHint || 'Shown on the confirmation page when registration is approved or auto-approved.'}>
        <textarea
          name="event[registeration_message]"
          defaultValue={event.registerationMessage || ''}
          rows={3}
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>

      <FormField label={t.pendingRegistrationMessage || 'Pending confirmation message'} hint={t.pendingRegistrationMessageHint || 'Shown on the confirmation page when approval is required.'}>
        <textarea
          name="event[pending_registration_message]"
          defaultValue={event.pendingRegistrationMessage || ''}
          rows={3}
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>

      <FormField label={t.emailSubject} hint={t.confirmationEmailHint || 'Sent when registration is auto-approved, or when an admin approves a pending registration.'}>
        <Input name="event[email_subject]" defaultValue={event.emailSubject || ''} className={formInput()} />
      </FormField>

      {/* Email Message — Rich Text Editor with source & personalization */}
      <FormField label={t.emailMessage}>
        <input type="hidden" name="event[email_message_en]" ref={hiddenEnRef} defaultValue={event.emailMessage_en || event.emailMessage || ''} />
        <input type="hidden" name="event[email_message_ar]" ref={hiddenArRef} defaultValue={event.emailMessage_ar || event.emailMessage || ''} />

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-[#E8E8E5] bg-[#FAFAFA] px-2 py-1.5">
          {/* EN / AR tabs */}
          <div className="flex items-center gap-0.5 mr-2">
            {(['en', 'ar'] as const).map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => {
                  // Sync current before switching
                  if (sourceMode) {
                    const src = activeSourceRef.current?.value || ''
                    if (activeEditorRef.current) activeEditorRef.current.innerHTML = src
                    if (activeHiddenRef.current) activeHiddenRef.current.value = src
                  }
                  setEmailLocale(loc)
                  setSourceMode(false)
                }}
                className={`h-7 px-2 rounded text-xs font-medium transition-colors ${emailLocale === loc ? 'bg-[#2383E2] text-white' : 'text-[#91918E] hover:bg-[#E8E8E5]'}`}
              >
                {loc.toUpperCase()}
              </button>
            ))}
          </div>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />

          {!sourceMode && (
            <>
              {([
                { cmd: 'bold', label: 'B', title: 'Bold', style: 'font-bold' },
                { cmd: 'italic', label: 'I', title: 'Italic', style: 'italic' },
                { cmd: 'underline', label: 'U', title: 'Underline', style: 'underline' },
              ] as const).map(b => (
                <button
                  key={b.cmd}
                  type="button"
                  title={b.title}
                  onClick={() => exec(b.cmd)}
                  className={`h-7 w-7 rounded text-xs ${b.style} hover:bg-[#E8E8E5] transition-colors text-[#37352F]`}
                >
                  {b.label}
                </button>
              ))}
              <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
              <select
                onChange={(e) => { if (e.target.value) exec('formatBlock', e.target.value); e.target.value = '' }}
                className="h-7 rounded border-0 bg-transparent px-1 text-xs text-[#37352F] cursor-pointer hover:bg-[#E8E8E5]"
                defaultValue=""
              >
                <option value="" disabled>Heading</option>
                <option value="h1">H1</option>
                <option value="h2">H2</option>
                <option value="h3">H3</option>
                <option value="p">Paragraph</option>
              </select>
              <label title="Text color" className="relative cursor-pointer" onMouseDown={saveSelection}>
                <span className="flex h-7 w-7 items-center justify-center rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]">A</span>
                <input
                  type="color"
                  className="sr-only"
                  onChange={(e) => { restoreSelection(); exec('foreColor', e.target.value) }}
                />
              </label>
              <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
            </>
          )}

          {/* Personalization tokens dropdown */}
          <div className="relative" ref={tokenMenuRef}>
            <button
              type="button"
              onClick={() => setShowTokenMenu(!showTokenMenu)}
              className="h-7 px-2 rounded text-xs font-medium hover:bg-[#E8E8E5] transition-colors text-[#2383E2] border border-[#2383E2]/30"
              title={t.personalizationTokens || 'Insert Personalization'}
            >
              {'{ }'}
            </button>
            {showTokenMenu && (
              <div className="absolute left-0 top-full mt-1 z-50 w-48 rounded-md border border-[#E8E8E5] bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                {PERSONALIZATION_TOKENS.map(({ token, label }) => (
                  <button
                    key={token}
                    type="button"
                    onClick={() => insertToken(token)}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-xs hover:bg-[#F1F1EF] transition-colors"
                  >
                    <span className="text-[#37352F]">{label}</span>
                    <span className="text-[#91918E] font-mono text-[10px]">{token}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Source code toggle */}
          <button
            type="button"
            onClick={toggleSourceMode}
            className={`h-7 px-2 rounded text-xs font-medium transition-colors ${sourceMode ? 'bg-[#37352F] text-white' : 'text-[#91918E] hover:bg-[#E8E8E5]'}`}
            title={t.sourceCode || 'Source Code'}
          >
            {'</>'}
          </button>
        </div>

        {/* Visual editor areas (EN / AR) */}
        {!sourceMode && (
          <>
            <div
              ref={editorEnRef}
              contentEditable
              onInput={syncEditorEn}
              onBlur={syncEditorEn}
              className="min-h-[180px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_p]:leading-relaxed [&_a]:text-[#2383E2] [&_a]:underline"
              style={{ display: emailLocale === 'en' ? undefined : 'none' }}
            />
            <div
              ref={editorArRef}
              contentEditable
              dir="rtl"
              onInput={syncEditorAr}
              onBlur={syncEditorAr}
              className="min-h-[180px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_p]:leading-relaxed [&_a]:text-[#2383E2] [&_a]:underline"
              style={{ display: emailLocale === 'ar' ? undefined : 'none' }}
            />
          </>
        )}

        {/* Source code textareas */}
        {sourceMode && (
          <>
            <textarea
              ref={sourceEnRef}
              defaultValue={editorEnRef.current?.innerHTML || hiddenEnRef.current?.value || ''}
              onChange={syncSource}
              rows={10}
              spellCheck={false}
              className="w-full rounded-b-md border border-[#E8E8E5] bg-[#FAFAFA] px-3 py-2 text-sm text-[#37352F] font-mono placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
              style={{ display: emailLocale === 'en' ? undefined : 'none' }}
            />
            <textarea
              ref={sourceArRef}
              defaultValue={editorArRef.current?.innerHTML || hiddenArRef.current?.value || ''}
              onChange={syncSource}
              rows={10}
              dir="rtl"
              spellCheck={false}
              className="w-full rounded-b-md border border-[#E8E8E5] bg-[#FAFAFA] px-3 py-2 text-sm text-[#37352F] font-mono placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
              style={{ display: emailLocale === 'ar' ? undefined : 'none' }}
            />
          </>
        )}

        {/* Token reference */}
        <div className="mt-2 rounded-md border border-[#E8E8E5] bg-[#FAFAFA] p-3 text-xs text-[#6B6B6B]">
          <h4 className="font-semibold text-[#37352F] mb-1.5">Available Personalization Tokens</h4>
          <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3 font-mono">
            {PERSONALIZATION_TOKENS.map(({ token, label }) => (
              <div key={token}><span className="text-[#2383E2]">{token}</span> — {label}</div>
            ))}
          </div>
        </div>
      </FormField>

      <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider pt-2">
        {t.pendingEmailSection || 'Pending approval email'}
      </h3>
      <p className="text-xs text-[#91918E]">
        {t.pendingEmailSectionHint || 'Sent instead of the confirmation email when a registration link requires approval.'}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={`${t.pendingEmailSubject || 'Pending email subject'} (EN)`}>
          <Input name="event[pending_email_subject_en]" defaultValue={event.pendingEmailSubject_en || event.pendingEmailSubject || ''} className={formInput()} />
        </FormField>
        <FormField label={`${t.pendingEmailSubject || 'Pending email subject'} (AR)`}>
          <Input name="event[pending_email_subject_ar]" defaultValue={event.pendingEmailSubject_ar || ''} className={formInput()} dir="rtl" />
        </FormField>
      </div>
      <FormField label={`${t.pendingEmailMessage || 'Pending email body'} (EN)`}>
        <textarea
          name="event[pending_email_message_en]"
          defaultValue={event.pendingEmailMessage_en || ''}
          rows={5}
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>
      <FormField label={`${t.pendingEmailMessage || 'Pending email body'} (AR)`}>
        <textarea
          name="event[pending_email_message_ar]"
          defaultValue={event.pendingEmailMessage_ar || ''}
          rows={5}
          dir="rtl"
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>

      <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider pt-2">
        {t.rejectedEmailSection || 'Rejected email'}
      </h3>
      <p className="text-xs text-[#91918E]">
        {t.rejectedEmailSectionHint || 'Sent when an admin rejects a registration.'}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={`${t.rejectedEmailSubject || 'Rejected email subject'} (EN)`}>
          <Input name="event[rejected_email_subject_en]" defaultValue={event.rejectedEmailSubject_en || event.rejectedEmailSubject || ''} className={formInput()} />
        </FormField>
        <FormField label={`${t.rejectedEmailSubject || 'Rejected email subject'} (AR)`}>
          <Input name="event[rejected_email_subject_ar]" defaultValue={event.rejectedEmailSubject_ar || ''} className={formInput()} dir="rtl" />
        </FormField>
      </div>
      <FormField label={`${t.rejectedEmailMessage || 'Rejected email body'} (EN)`}>
        <textarea
          name="event[rejected_email_message_en]"
          defaultValue={event.rejectedEmailMessage_en || ''}
          rows={5}
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>
      <FormField label={`${t.rejectedEmailMessage || 'Rejected email body'} (AR)`}>
        <textarea
          name="event[rejected_email_message_ar]"
          defaultValue={event.rejectedEmailMessage_ar || ''}
          rows={5}
          dir="rtl"
          className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
        />
      </FormField>

      <div className="flex items-center gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
        <input type="hidden" name="event[send_qr]" value={sendQr ? '1' : '0'} />
        <Switch checked={sendQr} onCheckedChange={setSendQr} />
        <Label className="text-sm text-[#37352F] cursor-pointer">{t.sendQr}</Label>
      </div>

      <FormField label={t.emailImage}>
        <div className="flex items-center gap-3">
          {event.emailImageUrl && (
            <img src={event.emailImageUrl} alt="Email" className="h-12 w-12 rounded-md border border-[#E8E8E5] object-contain" />
          )}
          <Input name="event[email_image]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Font                                                      */
/* ------------------------------------------------------------------ */

function FontSection({ event, fonts, urls, t }: { event: EventData; fonts: string[]; urls: SetupProps['urls']; t: Record<string, string> }) {
  const [selectedFont, setSelectedFont] = useState(event.font || '')
  const [saving, setSaving] = useState(false)

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'font', () => setSaving(false))
      }}
      className="space-y-5"
    >
      <FormField label={t.selectFont}>
        <select
          name="event[font]"
          value={selectedFont}
          onChange={(e) => setSelectedFont(e.target.value)}
          className={formInput('w-full')}
        >
          <option value="">—</option>
          {fonts.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>
              {f.replace(/['"]/g, '')}
            </option>
          ))}
        </select>
      </FormField>

      {/* Live preview */}
      {selectedFont && (
        <div className="rounded-md border border-[#E8E8E5] p-4 space-y-2" style={{ fontFamily: selectedFont }}>
          <p className="text-lg text-[#37352F]">The quick brown fox jumps over the lazy dog</p>
          <p className="text-lg text-[#37352F]" dir="rtl">هذا النص لمعاينة الخط المختار</p>
        </div>
      )}

      {event.customFontName && (
        <div className="rounded-md bg-[#F1F1EF] px-3 py-2 text-sm text-[#37352F]">
          {t.customFont}: <span className="font-medium">{event.customFontName}</span>
        </div>
      )}

      <FormField label={t.uploadCustomFont} hint={t.customFontHint}>
        <Input name="event[custom_font]" type="file" accept=".ttf,.otf,.woff,.woff2" className={formInput()} />
      </FormField>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Template                                                  */
/* ------------------------------------------------------------------ */

function TemplateSection({ event, urls, t }: { event: EventData; urls: SetupProps['urls']; t: Record<string, string> }) {
  const [template, setTemplate] = useState(event.template || 1)
  const [saving, setSaving] = useState(false)

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'template', () => setSaving(false))
      }}
      className="space-y-5"
    >
      <FormField label={t.selectTemplate}>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => setTemplate(num)}
              className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors cursor-pointer ${
                template === num
                  ? 'border-[#2383E2] bg-[#EBF5FF]'
                  : 'border-[#E8E8E5] hover:border-[#D3D3D0] bg-white'
              }`}
            >
              <LayoutTemplate className={`h-8 w-8 ${template === num ? 'text-[#2383E2]' : 'text-[#91918E]'}`} />
              <span className={`text-sm font-medium ${template === num ? 'text-[#2383E2]' : 'text-[#37352F]'}`}>
                {t.template} {num}
              </span>
            </button>
          ))}
        </div>
      </FormField>

      <input type="hidden" name="event[template]" value={template} />

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Badge Field Picker                                                 */
/* ------------------------------------------------------------------ */

const BADGE_NAME_FIELDS = ['title', 'first_name', 'last_name']

function normalizeBadgeRows(raw: string[] | string[][] | null | undefined): string[][] {
  if (!raw || raw.length === 0) return []
  if (Array.isArray(raw[0])) return raw as string[][]

  const rows: string[][] = []
  let buffer: string[] = []

  for (const field of raw as string[]) {
    if (BADGE_NAME_FIELDS.includes(field)) {
      buffer.push(field)
    } else {
      if (buffer.length) {
        rows.push(buffer)
        buffer = []
      }
      rows.push([field])
    }
  }
  if (buffer.length) rows.push(buffer)
  return rows
}

function BadgeFieldPicker({
  available,
  rows,
  onChange,
  t,
  namePrefix = 'event[badge_display_fields]',
}: {
  available: Array<{ field: string; label: string; isCustom: boolean }>
  rows: string[][]
  onChange: (rows: string[][]) => void
  t: Record<string, string>
  namePrefix?: string
}) {
  const selectedFields = rows.flat()
  const labelFor = (field: string) => available.find((a) => a.field === field)?.label || field
  const unusedFields = available.filter((a) => !selectedFields.includes(a.field))

  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir
    if (target < 0 || target >= rows.length) return
    const next = [...rows]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    onChange(next)
  }

  const removeRow = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx))
  }

  const removeFieldFromRow = (rowIdx: number, field: string) => {
    const next = rows
      .map((row, i) => (i === rowIdx ? row.filter((f) => f !== field) : row))
      .filter((row) => row.length > 0)
    onChange(next)
  }

  const addFieldToRow = (rowIdx: number, field: string) => {
    if (selectedFields.includes(field)) return
    const next = rows.map((row, i) => (i === rowIdx ? [...row, field] : row))
    onChange(next)
  }

  const addRow = (fields: string[] = []) => {
    const unique = fields.filter((f) => !selectedFields.includes(f))
    if (unique.length === 0) return
    onChange([...rows, unique])
  }

  const addNameLine = () => {
    addRow(BADGE_NAME_FIELDS.filter((f) => available.some((a) => a.field === f)))
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <input type="hidden" name={`${namePrefix}[]`} value="" />
      ) : (
        rows.map((row, rowIdx) => (
          <div key={`row-${rowIdx}`}>
            {row.map((field) => (
              <input
                key={`${rowIdx}-${field}`}
                type="hidden"
                name={`${namePrefix}[${rowIdx}][]`}
                value={field}
              />
            ))}
          </div>
        ))
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-[#91918E] italic">
          {t.noBadgeFieldsSelected || 'No fields selected — default badge layout will be used.'}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, rowIdx) => (
            <div
              key={`badge-row-${rowIdx}`}
              className="rounded-md border border-[#E8E8E5] bg-white p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-[#91918E] uppercase tracking-wider">
                  {t.badgeDisplayLine || 'Line'} {rowIdx + 1}{' '}
                  <span className="normal-case font-normal">
                    ({t.badgeDisplaySameLine || 'same line'})
                  </span>
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveRow(rowIdx, -1)}
                    disabled={rowIdx === 0}
                    className="text-[#91918E] hover:text-[#37352F] disabled:opacity-30 px-1"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => moveRow(rowIdx, 1)}
                    disabled={rowIdx === rows.length - 1}
                    className="text-[#91918E] hover:text-[#37352F] disabled:opacity-30 px-1"
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(rowIdx)}
                    className="text-red-400 hover:text-red-600 ml-1 text-xs"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {row.map((field) => {
                  const item = available.find((a) => a.field === field)
                  return (
                    <span
                      key={field}
                      className="inline-flex items-center gap-1 rounded-full border border-[#E8E8E5] bg-[#F7F7F5] px-3 py-1 text-xs text-[#37352F]"
                    >
                      {item?.label || labelFor(field)}
                      {item?.isCustom && (
                        <span className="text-[#91918E] bg-white rounded px-1 py-0.5">Custom</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFieldFromRow(rowIdx, field)}
                        className="text-red-400 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </span>
                  )
                })}
              </div>

              {unusedFields.length > 0 && (
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const field = e.target.value
                    if (!field) return
                    addFieldToRow(rowIdx, field)
                    e.target.value = ''
                  }}
                  className={formInput('h-8 text-xs max-w-xs')}
                >
                  <option value="">{t.addFieldToLine || 'Add field…'}</option>
                  {unusedFields.map((item) => (
                    <option key={item.field} value={item.field}>
                      {item.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {unusedFields.length > 0 && (
          <button
            type="button"
            onClick={() => addRow([unusedFields[0].field])}
            className="rounded-full border border-[#E8E8E5] bg-[#F7F7F5] px-3 py-1 text-xs text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
          >
            + {t.addBadgeLine || 'Add line'}
          </button>
        )}
        {BADGE_NAME_FIELDS.some((f) => available.some((a) => a.field === f) && !selectedFields.includes(f)) && (
          <button
            type="button"
            onClick={addNameLine}
            className="rounded-full border border-[#2383E2]/30 bg-[#2383E2]/5 px-3 py-1 text-xs text-[#2383E2] hover:bg-[#2383E2]/10 transition-colors"
          >
            + {t.addNameLine || 'Add name line'}
          </button>
        )}
      </div>

      {unusedFields.length > 0 && rows.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[#91918E] uppercase tracking-wider">
            {t.availableFields || 'Available Fields'}
          </p>
          <div className="flex flex-wrap gap-2">
            {unusedFields.map((item) => (
              <button
                key={item.field}
                type="button"
                onClick={() => addRow([item.field])}
                className="flex items-center gap-1 rounded-full border border-[#E8E8E5] bg-[#F7F7F5] px-3 py-1 text-xs text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
              >
                + {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Badge                                                     */
/* ------------------------------------------------------------------ */

function BadgeSection({ event, badges, badgeAvailableFields, urls, t }: { event: EventData; badges: BadgeItem[]; badgeAvailableFields: Array<{ field: string; label: string; isCustom: boolean }>; urls: SetupProps['urls']; t: Record<string, string> }) {
  const [printQr, setPrintQr] = useState(event.printQr)
  const [badgeRows, setBadgeRows] = useState<string[][]>(() => normalizeBadgeRows(event.badgeDisplayFields))
  const [physicalPrint, setPhysicalPrint] = useState(!!event.physicalBadgePrint)
  const [physicalPrintQr, setPhysicalPrintQr] = useState(event.physicalPrintQr ?? event.printQr)
  const [physicalRows, setPhysicalRows] = useState<string[][]>(() => {
    const saved = normalizeBadgeRows(event.physicalBadgeDisplayFields)
    return saved.length ? saved : normalizeBadgeRows(event.badgeDisplayFields)
  })
  const [saving, setSaving] = useState(false)
  const [defaultBadgeId, setDefaultBadgeId] = useState<number | null>(
    badges.find((b) => b.isDefault)?.id ?? badges[0]?.id ?? null
  )

  const enablePhysicalPrint = (enabled: boolean) => {
    setPhysicalPrint(enabled)
    if (!enabled) return
    if (physicalRows.length === 0) {
      setPhysicalRows(normalizeBadgeRows(event.badgeDisplayFields))
    }
  }

  return (
    <form
      onSubmit={(e) => {
        if (badges.length > 0 && defaultBadgeId === null) {
          e.preventDefault()
          alert(t.pleaseSelectDefault || 'Please check one as default')
          return
        }
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'badge', () => setSaving(false))
      }}
      className="space-y-5"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t.badgeWidth}>
          <Input name="event[badge_width]" type="number" step="0.1" defaultValue={event.badgeWidth || '3.5'} className={formInput()} />
        </FormField>
        <FormField label={t.badgeHeight}>
          <Input name="event[badge_height]" type="number" step="0.1" defaultValue={event.badgeHeight || '5.5'} className={formInput()} />
        </FormField>
        <FormField label={t.badgeTextPosition}>
          <Input name="event[badge_text_postion]" type="number" defaultValue={event.badgeTextPosition ?? 280} className={formInput()} />
        </FormField>
        <FormField label={t.badgeNameFontSize}>
          <Input name="event[badge_name_size]" type="number" defaultValue={event.badgeNameSize ?? 28} className={formInput()} />
        </FormField>
        <FormField label={t.badgeSecondaryFontSize}>
          <Input name="event[badge_secondary_size]" type="number" defaultValue={event.badgeSecondarySize ?? 18} className={formInput()} />
        </FormField>
        <FormField label={t.badgeTextAlignment}>
          <select
            name="event[badge_text_alignment]"
            defaultValue={event.badgeTextAlignment || 'center'}
            className={formInput('w-full')}
          >
            <option value="left">{t.alignLeft}</option>
            <option value="center">{t.alignCenter}</option>
            <option value="right">{t.alignRight}</option>
          </select>
        </FormField>
        <FormField label={t.badgeMarginLeft}>
          <Input name="event[badge_margin_left]" type="number" defaultValue={event.badgeMarginLeft ?? 25} className={formInput()} />
        </FormField>
        <FormField label={t.badgeMarginRight}>
          <Input name="event[badge_margin_right]" type="number" defaultValue={event.badgeMarginRight ?? 25} className={formInput()} />
        </FormField>
      </div>

      <div className="flex items-center gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
        <input type="hidden" name="event[print_qr]" value={printQr ? '1' : '0'} />
        <Switch checked={printQr} onCheckedChange={setPrintQr} />
        <Label className="text-sm text-[#37352F] cursor-pointer">{t.printQrCode}</Label>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
        <input type="hidden" name="event[physical_badge_print]" value={physicalPrint ? '1' : '0'} />
        <Switch checked={physicalPrint} onCheckedChange={enablePhysicalPrint} />
        <div>
          <Label className="text-sm text-[#37352F] cursor-pointer">{t.physicalBadgePrinting || 'Physical badge printing'}</Label>
          {t.physicalBadgePrintingHint && (
            <p className="text-xs text-[#91918E] mt-1">{t.physicalBadgePrintingHint}</p>
          )}
        </div>
      </div>

      {physicalPrint && (
        <div className="space-y-4 rounded-lg border border-[#E8E8E5] p-4">
          <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">
            {t.physicalBadgeLayout || 'Physical print layout'}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t.badgeWidth}>
              <Input name="event[physical_badge_width]" type="number" step="0.1" defaultValue={event.physicalBadgeWidth || event.badgeWidth || '3.5'} className={formInput()} />
            </FormField>
            <FormField label={t.badgeHeight}>
              <Input name="event[physical_badge_height]" type="number" step="0.1" defaultValue={event.physicalBadgeHeight || event.badgeHeight || '5.5'} className={formInput()} />
            </FormField>
            <FormField label={t.badgeTextPosition}>
              <Input name="event[physical_badge_text_postion]" type="number" defaultValue={event.physicalBadgeTextPosition ?? event.badgeTextPosition ?? 280} className={formInput()} />
            </FormField>
            <FormField label={t.badgeNameFontSize}>
              <Input name="event[physical_badge_name_size]" type="number" defaultValue={event.physicalBadgeNameSize ?? event.badgeNameSize ?? 28} className={formInput()} />
            </FormField>
            <FormField label={t.badgeSecondaryFontSize}>
              <Input name="event[physical_badge_secondary_size]" type="number" defaultValue={event.physicalBadgeSecondarySize ?? event.badgeSecondarySize ?? 18} className={formInput()} />
            </FormField>
            <FormField label={t.badgeTextAlignment}>
              <select
                name="event[physical_badge_text_alignment]"
                defaultValue={event.physicalBadgeTextAlignment || event.badgeTextAlignment || 'center'}
                className={formInput('w-full')}
              >
                <option value="left">{t.alignLeft}</option>
                <option value="center">{t.alignCenter}</option>
                <option value="right">{t.alignRight}</option>
              </select>
            </FormField>
            <FormField label={t.badgeMarginLeft}>
              <Input name="event[physical_badge_margin_left]" type="number" defaultValue={event.physicalBadgeMarginLeft ?? event.badgeMarginLeft ?? 25} className={formInput()} />
            </FormField>
            <FormField label={t.badgeMarginRight}>
              <Input name="event[physical_badge_margin_right]" type="number" defaultValue={event.physicalBadgeMarginRight ?? event.badgeMarginRight ?? 25} className={formInput()} />
            </FormField>
          </div>

          <div className="flex items-center gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
            <input type="hidden" name="event[physical_print_qr]" value={physicalPrintQr ? '1' : '0'} />
            <Switch checked={physicalPrintQr} onCheckedChange={setPhysicalPrintQr} />
            <Label className="text-sm text-[#37352F] cursor-pointer">{t.printQrCode}</Label>
          </div>

          <div className="space-y-3">
            <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">
              {t.badgeDisplayFields || 'Badge Display Fields'}
            </h3>
            {t.badgeDisplayFieldsHint && (
              <p className="text-xs text-[#91918E]">{t.badgeDisplayFieldsHint}</p>
            )}
            <BadgeFieldPicker
              available={badgeAvailableFields}
              rows={physicalRows}
              onChange={setPhysicalRows}
              t={t}
              namePrefix="event[physical_badge_display_fields]"
            />
          </div>
        </div>
      )}

      {/* Badge types — upload background + set default */}
      {badges.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.badgeTypes}</h3>
          <div className="space-y-3">
            {badges.map((badge, idx) => (
              <div key={badge.id} className="rounded-lg border border-[#E8E8E5] p-4 space-y-3">
                <input type="hidden" name={`event[badges_attributes][${idx}][id]`} value={badge.id} />
                <input type="hidden" name={`event[badges_attributes][${idx}][name]`} value={badge.name} />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-[#91918E]" />
                    <span className="text-sm font-medium text-[#37352F]">{badge.name}</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="hidden" name={`event[badges_attributes][${idx}][default]`} value="0" />
                    <input
                      type="checkbox"
                      name={`event[badges_attributes][${idx}][default]`}
                      value="1"
                      checked={defaultBadgeId === badge.id}
                      onChange={() => setDefaultBadgeId(badge.id)}
                      className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
                    />
                    <span className="text-xs text-[#91918E]">{t.default}</span>
                  </label>
                </div>

                {/* Current background preview */}
                {badge.badgeBackgroundUrl && (
                  <img
                    src={badge.badgeBackgroundUrl}
                    alt={badge.name}
                    className="h-24 rounded-md border border-[#E8E8E5] object-contain"
                  />
                )}

                {/* Upload new background */}
                <FormField label={t.badgeBackground}>
                  <Input
                    name={`event[badges_attributes][${idx}][badge_background]`}
                    type="file"
                    accept="image/jpeg,image/png"
                    className={formInput()}
                  />
                </FormField>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Badge display fields */}
      <div className="space-y-3">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">
          {t.badgeDisplayFields || 'Badge Display Fields'}
        </h3>
        {t.badgeDisplayFieldsHint && (
          <p className="text-xs text-[#91918E]">{t.badgeDisplayFieldsHint}</p>
        )}
        <BadgeFieldPicker
          available={badgeAvailableFields}
          rows={badgeRows}
          onChange={setBadgeRows}
          t={t}
        />
      </div>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Event Images                                              */
/* ------------------------------------------------------------------ */

function EventImagesSection({ event, urls, t }: { event: EventData; urls: SetupProps['urls']; t: Record<string, string> }) {
  const [saving, setSaving] = useState(false)

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'event_images', () => setSaving(false))
      }}
      className="space-y-5"
    >
      <FormField label={t.logo}>
        <div className="flex items-center gap-3">
          {event.logoUrl && (
            <img src={event.logoUrl} alt="Logo" className="h-16 w-16 rounded-md border border-[#E8E8E5] object-contain" />
          )}
          <Input name="event[logo]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <FormField label={t.eventImagesLabel} hint={t.eventImagesHint}>
        <Input name="event[image][]" type="file" accept="image/jpeg,image/png" multiple className={formInput()} />
        {event.eventImageUrls.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            {event.eventImageUrls.map((img) => (
              <img key={img.id} src={img.url} alt="" className="h-20 w-full rounded border border-[#E8E8E5] object-cover" />
            ))}
          </div>
        )}
      </FormField>

      <FormField label={t.aboutBackground} hint={t.aboutBackgroundHint}>
        <div className="flex items-center gap-3">
          {event.aboutBackgroundUrl && (
            <img src={event.aboutBackgroundUrl} alt="" className="h-16 w-24 rounded-md border border-[#E8E8E5] object-cover" />
          )}
          <Input name="event[about_background]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Speakers (inline CRUD)                                    */
/* ------------------------------------------------------------------ */

function SpeakersSection({
  speakers,
  eventId,
  urls,
  t,
}: {
  speakers: SpeakerItem[]
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)
    // Strip empty file inputs
    const emptyKeys: string[] = []
    fd.forEach((v, k) => { if (v instanceof File && v.size === 0) emptyKeys.push(k) })
    emptyKeys.forEach((k) => fd.delete(k))

    router.post(urls.createSpeaker, fd as any, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => { setSaving(false); setShowForm(false) },
    })
  }

  function handleDelete(deleteUrl: string) {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(deleteUrl, { preserveScroll: true })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.speakers}</h3>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {showForm ? t.cancel : t.addNew}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-lg border border-[#2383E2]/30 bg-[#FAFBFF] p-4 space-y-3">
          <input type="hidden" name="speaker[event_id]" value={eventId} />
          <FormField label={t.speakerName || t.eventName?.replace('Event', 'Speaker') || 'Name'}>
            <Input name="speaker[name]" className={formInput()} required />
          </FormField>
          <FormField label={t.speakerDescription || 'Description'}>
            <textarea
              name="speaker[description]"
              rows={2}
              className="w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
            />
          </FormField>
          <FormField label={t.speakerImage || 'Image'}>
            <Input name="speaker[image]" type="file" accept="image/jpeg,image/png" className={formInput()} />
          </FormField>
          <div className="flex justify-end">
            <SaveButton t={t} saving={saving} />
          </div>
        </form>
      )}

      {speakers.length === 0 && !showForm ? (
        <div className="rounded-md border border-dashed border-[#E8E8E5] px-4 py-8 text-center">
          <Mic2 className="h-8 w-8 text-[#D3D3D0] mx-auto mb-2" />
          <p className="text-sm text-[#91918E]">{t.noItems}</p>
        </div>
      ) : (
        speakers.length > 0 && (
          <div className="rounded-md border border-[#E8E8E5] divide-y divide-[#F1F1EF]">
            {speakers.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className="h-8 w-8 rounded-full border border-[#E8E8E5] object-cover" />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F1EF] text-[#91918E]">
                    <Mic2 className="h-4 w-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-[#37352F] block truncate">{item.name}</span>
                  {item.description && (
                    <span className="text-xs text-[#91918E] block truncate">{item.description}</span>
                  )}
                </div>
                <button type="button" onClick={() => handleDelete(item.deleteUrl)} className="text-[#EB5757] hover:text-[#D04040] p-1 cursor-pointer shrink-0">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Partners (inline CRUD)                                    */
/* ------------------------------------------------------------------ */

function PartnersSection({
  partners,
  eventId,
  urls,
  t,
}: {
  partners: PartnerItem[]
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)
    const emptyKeys: string[] = []
    fd.forEach((v, k) => { if (v instanceof File && v.size === 0) emptyKeys.push(k) })
    emptyKeys.forEach((k) => fd.delete(k))
    router.post(urls.createPartner, fd as any, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => { setSaving(false); setShowForm(false) },
    })
  }

  function handleDelete(deleteUrl: string) {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(deleteUrl, { preserveScroll: true })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.partners}</h3>
        <button type="button" onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer">
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {showForm ? t.cancel : t.addNew}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-lg border border-[#2383E2]/30 bg-[#FAFBFF] p-4 space-y-3">
          <input type="hidden" name="partner[event_id]" value={eventId} />
          <FormField label={t.partnerName || 'Name'}>
            <Input name="partner[name]" className={formInput()} required />
          </FormField>
          <FormField label={t.partnerImage || 'Image'}>
            <Input name="partner[image]" type="file" accept="image/jpeg,image/png" className={formInput()} />
          </FormField>
          <div className="flex justify-end">
            <SaveButton t={t} saving={saving} />
          </div>
        </form>
      )}

      {partners.length === 0 && !showForm ? (
        <div className="rounded-md border border-dashed border-[#E8E8E5] px-4 py-8 text-center">
          <Handshake className="h-8 w-8 text-[#D3D3D0] mx-auto mb-2" />
          <p className="text-sm text-[#91918E]">{t.noItems}</p>
        </div>
      ) : (
        partners.length > 0 && (
          <div className="rounded-md border border-[#E8E8E5] divide-y divide-[#F1F1EF]">
            {partners.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className="h-8 w-8 rounded-full border border-[#E8E8E5] object-cover" />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F1EF] text-[#91918E]">
                    <Handshake className="h-4 w-4" />
                  </div>
                )}
                <span className="text-sm text-[#37352F] flex-1 truncate">{item.name}</span>
                <button type="button" onClick={() => handleDelete(item.deleteUrl)} className="text-[#EB5757] hover:text-[#D04040] p-1 cursor-pointer shrink-0">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: External Links (inline CRUD)                              */
/* ------------------------------------------------------------------ */

function ExternalLinksSection({
  externalLinks,
  eventId,
  urls,
  t,
}: {
  externalLinks: ExternalLinkItem[]
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)
    router.post(urls.createExternalLink, fd as any, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => { setSaving(false); setShowForm(false) },
    })
  }

  function handleDelete(deleteUrl: string) {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(deleteUrl, { preserveScroll: true })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.externalLinks}</h3>
        <button type="button" onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer">
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {showForm ? t.cancel : t.addNew}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-lg border border-[#2383E2]/30 bg-[#FAFBFF] p-4 space-y-3">
          <input type="hidden" name="external_link[event_id]" value={eventId} />
          <FormField label={t.linkName || 'Name'}>
            <Input name="external_link[name]" className={formInput()} required />
          </FormField>
          <FormField label={t.linkUrl || 'URL'}>
            <Input name="external_link[link]" className={formInput()} placeholder="https://..." required />
          </FormField>
          <div className="flex justify-end">
            <SaveButton t={t} saving={saving} />
          </div>
        </form>
      )}

      {externalLinks.length === 0 && !showForm ? (
        <div className="rounded-md border border-dashed border-[#E8E8E5] px-4 py-8 text-center">
          <ExternalLinkIcon className="h-8 w-8 text-[#D3D3D0] mx-auto mb-2" />
          <p className="text-sm text-[#91918E]">{t.noItems}</p>
        </div>
      ) : (
        externalLinks.length > 0 && (
          <div className="rounded-md border border-[#E8E8E5] divide-y divide-[#F1F1EF]">
            {externalLinks.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                <ExternalLinkIcon className="h-4 w-4 text-[#91918E] shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-[#37352F] block truncate">{item.name}</span>
                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2383E2] hover:underline truncate block">
                    {item.link}
                  </a>
                </div>
                <button type="button" onClick={() => handleDelete(item.deleteUrl)} className="text-[#EB5757] hover:text-[#D04040] p-1 cursor-pointer shrink-0">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Social Links (editable form)                              */
/* ------------------------------------------------------------------ */

function SocialLinksSection({
  socialLink,
  eventId,
  urls,
  t,
}: {
  socialLink: SocialLinkData | null
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [saving, setSaving] = useState(false)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)

    if (socialLink?.id && socialLink?.updateUrl) {
      router.put(socialLink.updateUrl, fd as any, {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    } else {
      router.post(urls.createSocialLink, fd as any, {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    }
  }

  const platforms = [
    { key: 'twitter', label: 'Twitter / X' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'youtube', label: 'YouTube' },
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.socialLinks}</h3>
      <input type="hidden" name="social_link[event_id]" value={eventId} />

      <div className="space-y-3">
        {platforms.map((p) => (
          <FormField key={p.key} label={p.label}>
            <Input
              name={`social_link[${p.key}]`}
              defaultValue={(socialLink as any)?.[p.key] || ''}
              placeholder="https://..."
              className={formInput()}
            />
          </FormField>
        ))}
      </div>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Venue (editable form)                                     */
/* ------------------------------------------------------------------ */

function VenueSection({
  venue,
  eventId,
  urls,
  t,
}: {
  venue: VenueData | null
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [saving, setSaving] = useState(false)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)
    // Strip empty file inputs
    const emptyKeys: string[] = []
    fd.forEach((v, k) => { if (v instanceof File && v.size === 0) emptyKeys.push(k) })
    emptyKeys.forEach((k) => fd.delete(k))

    if (venue?.updateUrl) {
      router.put(venue.updateUrl, fd as any, {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    } else {
      router.post(urls.createVenue, fd as any, {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    }
  }

  function handleDelete() {
    if (!venue?.deleteUrl) return
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(venue.deleteUrl, { preserveScroll: true })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.venue}</h3>
        {venue && (
          <button type="button" onClick={handleDelete} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#EB5757] hover:bg-[#FBE4E4] transition-colors cursor-pointer">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <input type="hidden" name="venue[event_id]" value={eventId} />

      <FormField label={t.venueName}>
        <Input name="venue[name]" defaultValue={venue?.name || ''} className={formInput()} required />
      </FormField>

      <FormField label={t.venueLocation} hint={t.venueLocationHint || 'Google Maps URL'}>
        <Input name="venue[location]" defaultValue={venue?.location || ''} className={formInput()} placeholder="https://maps.google.com/..." />
      </FormField>

      <FormField label={t.venueImages || 'Images'}>
        <Input name="venue[image][]" type="file" accept="image/jpeg,image/png" multiple className={formInput()} />
      </FormField>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Agenda (full: agenda + days + day items)                   */
/* ------------------------------------------------------------------ */

function CopyRegistrationLinkButton({
  url,
  t,
  compact = false,
}: {
  url: string
  t: Record<string, string>
  compact?: boolean
}) {
  const [copied, setCopied] = useState(false)

  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(url).then(() => {
          setCopied(true)
          window.setTimeout(() => setCopied(false), 2000)
        })
      }}
      className={
        compact
          ? 'inline-flex items-center gap-1.5 rounded-md border border-[#E8E8E5] bg-white px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer'
          : 'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer'
      }
      title={url}
    >
      {copied ? <Copy className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
      {copied ? (t.copied || 'Copied!') : (t.copyLink || 'Copy link')}
    </button>
  )
}

function DayCard({
  day,
  t,
  groupSessions = true,
}: {
  day: DayData
  urls?: SetupProps['urls']
  t: Record<string, string>
  groupSessions?: boolean
}) {
  const [items, setItems] = useState<Array<{
    key: number
    id: number | null
    descriptionEn: string
    descriptionAr: string
    time: string
    endTime: string
    capacity: string
    groupLabelEn: string
    groupLabelAr: string
    _destroy: boolean
  }>>(
    day.dayItems.map((di, i) => ({
      key: i,
      id: di.id,
      descriptionEn: di.descriptionEn || di.description || '',
      descriptionAr: di.descriptionAr || '',
      time: di.time,
      endTime: di.endTime || '',
      capacity: di.capacity != null ? String(di.capacity) : '',
      groupLabelEn: di.groupLabelEn || di.groupLabel || '',
      groupLabelAr: di.groupLabelAr || '',
      _destroy: false,
    }))
  )
  const [dayDate, setDayDate] = useState(day.date || '')
  const [nextKey, setNextKey] = useState(day.dayItems.length)
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setDayDate(day.date || '')
  }, [day.date])
  const [saving, setSaving] = useState(false)
  const registrationUrlById = new Map(
    day.dayItems.filter((di) => di.registrationUrl).map((di) => [di.id, di.registrationUrl as string]),
  )

  function addItem() {
    setItems((prev) => [...prev, { key: nextKey, id: null, descriptionEn: '', descriptionAr: '', time: '', endTime: '', capacity: '', groupLabelEn: '', groupLabelAr: '', _destroy: false }])
    setNextKey((k) => k + 1)
    setCollapsed(false)
  }

  function updateItem(key: number, updates: Partial<typeof items[0]>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...updates } : it)))
  }

  function removeItem(key: number) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, _destroy: true } : it)))
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    e.currentTarget.querySelectorAll('[contenteditable="true"]').forEach((el) => {
      el.dispatchEvent(new Event('blur', { bubbles: true }))
    })
    setSaving(true)
    const fd = new FormData(e.currentTarget)
    router.put(day.updateUrl, fd as any, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  function handleDeleteDay() {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(day.deleteUrl, { preserveScroll: true })
  }

  const visibleItems = items.filter((it) => !it._destroy)
  const sessionCountLabel = `${visibleItems.length} ${
    visibleItems.length === 1
      ? (t.sessionSingular || t.session || 'session')
      : (t.sessions || 'sessions')
  }`

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-[#E8E8E5] bg-white overflow-hidden shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-[#F7F7F5] border-b border-[#E8E8E5]">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left cursor-pointer"
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-[#91918E]" />
          <span className="text-sm font-semibold text-[#37352F]">
            {t.dayNumber} {day.number}
          </span>
          <span className="hidden text-xs text-[#91918E] sm:inline">· {sessionCountLabel}</span>
          <ChevronDown
            className={`ms-auto h-4 w-4 shrink-0 text-[#91918E] transition-transform ${collapsed ? '' : 'rotate-180'}`}
          />
        </button>
        <label className="flex items-center gap-2">
          <span className="sr-only">{t.dayDate || 'Date'}</span>
          <input
            type="date"
            name="day[date]"
            value={dayDate}
            onChange={(e) => setDayDate(e.target.value)}
            className="rounded-md border border-[#E8E8E5] bg-white px-2 py-1.5 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
          />
        </label>
        <button
          type="button"
          onClick={handleDeleteDay}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-[#EB5757] hover:bg-[#FBE4E4] transition-colors cursor-pointer"
        >
          <Trash2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t.deleteDay}</span>
        </button>
      </div>

      <input type="hidden" name="day[number]" value={day.number} />

      <div className={collapsed ? 'hidden' : 'p-4 space-y-4'}>
          {visibleItems.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#E8E8E5] px-4 py-8 text-center">
              <p className="text-sm text-[#91918E]">{t.noDayItems}</p>
            </div>
          )}

          <div className="space-y-3">
            {items.map((it, idx) => {
              if (it._destroy) {
                return it.id ? (
                  <div key={it.key} className="hidden">
                    <input type="hidden" name={`day[day_items_attributes][${idx}][id]`} value={it.id} />
                    <input type="hidden" name={`day[day_items_attributes][${idx}][_destroy]`} value="1" />
                  </div>
                ) : null
              }

              const sessionIndex = visibleItems.findIndex((v) => v.key === it.key) + 1
              const registrationUrl = it.id ? registrationUrlById.get(it.id) : undefined

              return (
                <div
                  key={it.key}
                  className="rounded-xl border border-[#E8E8E5] bg-[#FBFBFB] p-4 shadow-sm space-y-3"
                >
                  {it.id && <input type="hidden" name={`day[day_items_attributes][${idx}][id]`} value={it.id} />}

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-[#37352F]">
                      {(t.sessionNumber || 'Session %{n}').replace('%{n}', String(sessionIndex))}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeItem(it.key)}
                      className="inline-flex items-center justify-center rounded-md p-1.5 text-[#EB5757] hover:bg-[#FBE4E4] transition-colors cursor-pointer"
                      title={t.delete || 'Delete'}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[#91918E]">
                      {t.sessionTitleDescription || t.description || 'Session title / description'}
                    </Label>
                    <div className="grid gap-3 lg:grid-cols-2">
                      <div className="space-y-1">
                        <span className="block text-[11px] font-medium uppercase tracking-wide text-[#C4C4C0]">EN</span>
                        <AgendaItemDescriptionEditor
                          name={`day[day_items_attributes][${idx}][description_en]`}
                          value={it.descriptionEn}
                          onChange={(html) => updateItem(it.key, { descriptionEn: html })}
                          required={!it.descriptionAr?.replace(/<[^>]+>/g, '').trim()}
                          className="min-h-[5.5rem]"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[11px] font-medium uppercase tracking-wide text-[#C4C4C0]">AR</span>
                        <AgendaItemDescriptionEditor
                          name={`day[day_items_attributes][${idx}][description_ar]`}
                          value={it.descriptionAr}
                          onChange={(html) => updateItem(it.key, { descriptionAr: html })}
                          required={!it.descriptionEn?.replace(/<[^>]+>/g, '').trim()}
                          className="min-h-[5.5rem]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`grid gap-3 sm:grid-cols-2 ${groupSessions ? 'lg:grid-cols-4' : ''}`}>
                    {groupSessions && (
                      <>
                    <label className="block space-y-1.5">
                      <span className="block text-xs font-medium text-[#91918E]">
                        {(t.sessionGroup || 'Group')} (EN)
                      </span>
                      <input
                        type="text"
                        name={`day[day_items_attributes][${idx}][group_label_en]`}
                        value={it.groupLabelEn}
                        onChange={(e) => updateItem(it.key, { groupLabelEn: e.target.value })}
                        placeholder={t.sessionGroupPlaceholder || 'e.g. Opening'}
                        className="w-full rounded-md border border-[#E8E8E5] bg-white px-2.5 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                      />
                    </label>
                    <label className="block space-y-1.5">
                      <span className="block text-xs font-medium text-[#91918E]">
                        {(t.sessionGroup || 'Group')} (AR)
                      </span>
                      <input
                        type="text"
                        name={`day[day_items_attributes][${idx}][group_label_ar]`}
                        value={it.groupLabelAr}
                        onChange={(e) => updateItem(it.key, { groupLabelAr: e.target.value })}
                        placeholder={t.sessionGroupPlaceholderAr || 'مثال: الافتتاح'}
                        dir="rtl"
                        className="w-full rounded-md border border-[#E8E8E5] bg-white px-2.5 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                      />
                    </label>
                      </>
                    )}
                    <label className="block space-y-1.5">
                      <span className="block text-xs font-medium text-[#91918E]">{t.startsAt || t.startTime || t.time || 'Starts at'}</span>
                      <input
                        type="time"
                        name={`day[day_items_attributes][${idx}][time]`}
                        value={it.time}
                        onChange={(e) => updateItem(it.key, { time: e.target.value })}
                        className="w-full rounded-md border border-[#E8E8E5] bg-white px-2.5 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                        required
                      />
                    </label>
                    <label className="block space-y-1.5">
                      <span className="block text-xs font-medium text-[#91918E]">{t.endsAt || t.endTime || 'Ends at'}</span>
                      <input
                        type="time"
                        name={`day[day_items_attributes][${idx}][end_time]`}
                        value={it.endTime}
                        onChange={(e) => updateItem(it.key, { endTime: e.target.value })}
                        className="w-full rounded-md border border-[#E8E8E5] bg-white px-2.5 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                      />
                    </label>
                    <label className="block space-y-1.5 sm:col-span-2">
                      <span className="block text-xs font-medium text-[#91918E]">{t.registrationLimit || 'Registration limit'}</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        name={`day[day_items_attributes][${idx}][capacity]`}
                        value={it.capacity}
                        onChange={(e) => updateItem(it.key, { capacity: e.target.value })}
                        placeholder={t.registrationLimitHint || 'Leave blank for unlimited'}
                        className="w-full rounded-md border border-[#E8E8E5] bg-white px-2.5 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                      />
                    </label>
                    <div className="block space-y-1.5 sm:col-span-2 lg:col-span-4">
                      <span className="block text-xs font-medium text-[#91918E]">{t.sessionRegistrationLink || 'Registration link'}</span>
                      {registrationUrl ? (
                        <div className="flex min-h-[2.375rem] items-center gap-2 rounded-md border border-[#E8E8E5] bg-white px-2.5 py-1.5">
                          <Link2 className="h-3.5 w-3.5 shrink-0 text-[#91918E]" />
                          <span className="min-w-0 flex-1 truncate text-xs text-[#37352F]" title={registrationUrl}>
                            {registrationUrl}
                          </span>
                          <CopyRegistrationLinkButton url={registrationUrl} t={t} compact />
                        </div>
                      ) : (
                        <div className="flex min-h-[2.375rem] items-center rounded-md border border-dashed border-[#E8E8E5] px-2.5 py-1.5 text-xs text-[#C4C4C0]">
                          {t.saveToGetLink || 'Save day to get link'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <button
            type="button"
            onClick={addItem}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#B8D4F5] bg-[#F5F9FF] px-4 py-3 text-sm font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            {t.addSession || t.addItem || 'Add session'}
          </button>

          <div className="flex justify-end border-t border-[#F1F1EF] pt-3">
            <SaveButton t={t} saving={saving} />
          </div>
      </div>
    </form>
  )
}

function AgendaSection({
  agenda,
  eventId,
  urls,
  t,
  groupSessions = true,
}: {
  agenda: AgendaData | null
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
  groupSessions?: boolean
}) {
  const [saving, setSaving] = useState(false)

  function handleAgendaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)

    if (agenda?.updateUrl) {
      router.put(agenda.updateUrl, fd as any, {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    } else {
      router.post(urls.createAgenda, fd as any, {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    }
  }

  function handleDeleteAgenda() {
    if (!agenda?.deleteUrl) return
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(agenda.deleteUrl, { preserveScroll: true })
  }

  return (
    <div className="space-y-6">
      {/* Agenda settings form */}
      <form onSubmit={handleAgendaSubmit} className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.schedule}</h3>
          {agenda && (
            <button type="button" onClick={handleDeleteAgenda} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#EB5757] hover:bg-[#FBE4E4] transition-colors cursor-pointer">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <input type="hidden" name="agenda[event_id]" value={eventId} />

        <FormField label={t.numberOfDays}>
          <Input
            name="agenda[number_of_days]"
            type="number"
            min="1"
            defaultValue={agenda?.numberOfDays ?? ''}
            className={formInput()}
            required
          />
        </FormField>

        <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
          <SaveButton t={t} saving={saving} />
        </div>
      </form>

      {/* Day cards with day items */}
      {agenda && agenda.days && agenda.days.length > 0 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-[#37352F]">{t.daysAndSessions || t.agendaDays}</h3>
            <p className="mt-0.5 text-sm text-[#91918E]">
              {t.daysAndSessionsHint || 'Build the agenda and create registration links for each session.'}
            </p>
          </div>
          {agenda.days.map((day) => (
            <DayCard key={day.id} day={day} urls={urls} t={t} groupSessions={groupSessions} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Event Files (inline CRUD)                                 */
/* ------------------------------------------------------------------ */

function EventFilesSection({
  eventFiles,
  eventId,
  urls,
  t,
}: {
  eventFiles: EventFileItem[]
  eventId: number
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    const fd = new FormData(e.currentTarget)
    // strip empty file inputs
    const emptyKeys: string[] = []
    fd.forEach((v, k) => { if (v instanceof File && v.size === 0) emptyKeys.push(k) })
    emptyKeys.forEach((k) => fd.delete(k))
    router.post(urls.createEventFile, fd as any, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => { setSaving(false); setShowForm(false) },
    })
  }

  function handleDelete(deleteUrl: string) {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(deleteUrl, { preserveScroll: true })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.eventFiles}</h3>
        <button type="button" onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer">
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {showForm ? t.cancel : t.addNew}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-lg border border-[#2383E2]/30 bg-[#FAFBFF] p-4 space-y-3">
          <input type="hidden" name="event_file[event_id]" value={eventId} />
          <FormField label={t.fileName || 'File Name'}>
            <Input name="event_file[file_name]" className={formInput()} required />
          </FormField>
          <FormField label={t.fileUpload || 'File'}>
            <Input name="event_file[file]" type="file" accept=".pdf,.doc,.docx" className={formInput()} />
          </FormField>
          <div className="flex justify-end">
            <SaveButton t={t} saving={saving} />
          </div>
        </form>
      )}

      {eventFiles.length === 0 && !showForm ? (
        <div className="rounded-md border border-dashed border-[#E8E8E5] px-4 py-8 text-center">
          <FileText className="h-8 w-8 text-[#D3D3D0] mx-auto mb-2" />
          <p className="text-sm text-[#91918E]">{t.noItems}</p>
        </div>
      ) : (
        eventFiles.length > 0 && (
          <div className="rounded-md border border-[#E8E8E5] divide-y divide-[#F1F1EF]">
            {eventFiles.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                <FileText className="h-4 w-4 text-[#91918E] shrink-0" />
                <span className="text-sm text-[#37352F] flex-1 truncate">{item.fileName || `File #${item.id}`}</span>
                <button type="button" onClick={() => handleDelete(item.deleteUrl)} className="text-[#EB5757] hover:text-[#D04040] p-1 cursor-pointer shrink-0">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section: Registration Design                                       */
/* ------------------------------------------------------------------ */

function RegistrationDesignSection({
  event,
  urls,
  t,
}: {
  event: EventData
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [saving, setSaving] = useState(false)
  const [bgColor, setBgColor] = useState(event.regBgColor || '#F7F7F5')
  const [textColor, setTextColor] = useState(event.regTextColor || '#37352F')
  const [cardBg, setCardBg] = useState(event.regCardBgColor || '#FFFFFF')
  const [buttonColor, setButtonColor] = useState(event.regButtonColor || '#2383E2')
  const [buttonTextColor, setButtonTextColor] = useState(event.regButtonTextColor || '#FFFFFF')
  const [inputBorderColor, setInputBorderColor] = useState(event.regInputBorderColor || '#E8E8E5')
  const [formWidth, setFormWidth] = useState(event.regFormWidth || 'medium')
  const [formAlignment, setFormAlignment] = useState(event.regFormAlignment || 'center')
  const [mobileFormAlignment, setMobileFormAlignment] = useState(event.regMobileFormAlignment || 'center')
  const [showLangSwitcher, setShowLangSwitcher] = useState(event.regShowLangSwitcher)
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(event.settings?.registration?.show_privacy_policy === true)
  const [customCss, setCustomCss] = useState(event.regCustomCss || '')
  const [policyLocale, setPolicyLocale] = useState<'en' | 'ar'>('en')
  const [headerLocale, setHeaderLocale] = useState<'en' | 'ar'>('en')
  const [footerLocale, setFooterLocale] = useState<'en' | 'ar'>('en')
  const [headerPlacement, setHeaderPlacement] = useState<'inside' | 'outside'>(
    event.settings?.registration?.header_placement === 'outside' ? 'outside' : 'inside'
  )
  const [footerPlacement, setFooterPlacement] = useState<'inside' | 'outside'>(
    event.settings?.registration?.footer_placement === 'outside' ? 'outside' : 'inside'
  )

  function removeAttachment(field: 'registration_page_banner' | 'registration_page_mobile_banner' | 'registration_page_desktop_design' | 'registration_page_mobile_design') {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(urls.deleteEventImageAttachment, {
      data: { [field]: '1' },
      preserveScroll: true,
      preserveState: true,
    })
  }

  const defaultHeader = `<h2 style="text-align:center;color:${event.primaryColor || '#2383E2'}">${event.name || ''}</h2>${event.about ? `<p style="text-align:center;opacity:0.7">${event.about}</p>` : ''}`

  const sanitizeRichTextHtml = (html: string) => {
    if (typeof window === 'undefined') return html

    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc.querySelectorAll('style, script, iframe, link[rel="stylesheet"], meta[http-equiv="refresh"]').forEach((node) => node.remove())
    return doc.body.innerHTML
  }

  const editorEnRef = useRef<HTMLDivElement>(null)
  const editorArRef = useRef<HTMLDivElement>(null)
  const hiddenEnRef = useRef<HTMLInputElement>(null)
  const hiddenArRef = useRef<HTMLInputElement>(null)
  const policyEditorEnRef = useRef<HTMLDivElement>(null)
  const policyEditorArRef = useRef<HTMLDivElement>(null)
  const policyHiddenEnRef = useRef<HTMLInputElement>(null)
  const policyHiddenArRef = useRef<HTMLInputElement>(null)
  const footerEditorEnRef = useRef<HTMLDivElement>(null)
  const footerEditorArRef = useRef<HTMLDivElement>(null)
  const footerHiddenEnRef = useRef<HTMLInputElement>(null)
  const footerHiddenArRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editorEnRef.current) {
      editorEnRef.current.innerHTML = sanitizeRichTextHtml(event.regHeaderHtml_en || event.regHeaderHtml || defaultHeader)
    }
    if (editorArRef.current) {
      editorArRef.current.innerHTML = sanitizeRichTextHtml(event.regHeaderHtml_ar || event.regHeaderHtml || defaultHeader)
    }
    if (policyEditorEnRef.current) {
      policyEditorEnRef.current.innerHTML = sanitizeRichTextHtml(event.eventTerms_en || event.eventTerms || '')
    }
    if (policyEditorArRef.current) {
      policyEditorArRef.current.innerHTML = sanitizeRichTextHtml(event.eventTerms_ar || event.eventTerms || '')
    }
    if (footerEditorEnRef.current) {
      footerEditorEnRef.current.innerHTML = sanitizeRichTextHtml(event.regFooterHtml_en || event.regFooterHtml || '')
    }
    if (footerEditorArRef.current) {
      footerEditorArRef.current.innerHTML = sanitizeRichTextHtml(event.regFooterHtml_ar || event.regFooterHtml || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const syncEditorEn = () => {
    if (editorEnRef.current && hiddenEnRef.current) {
      const sanitized = sanitizeRichTextHtml(editorEnRef.current.innerHTML)
      if (editorEnRef.current.innerHTML !== sanitized) editorEnRef.current.innerHTML = sanitized
      hiddenEnRef.current.value = sanitized
    }
  }
  const syncEditorAr = () => {
    if (editorArRef.current && hiddenArRef.current) {
      const sanitized = sanitizeRichTextHtml(editorArRef.current.innerHTML)
      if (editorArRef.current.innerHTML !== sanitized) editorArRef.current.innerHTML = sanitized
      hiddenArRef.current.value = sanitized
    }
  }
  const syncPolicyEn = () => {
    if (policyEditorEnRef.current && policyHiddenEnRef.current) {
      const sanitized = sanitizeRichTextHtml(policyEditorEnRef.current.innerHTML)
      if (policyEditorEnRef.current.innerHTML !== sanitized) policyEditorEnRef.current.innerHTML = sanitized
      policyHiddenEnRef.current.value = sanitized
    }
  }
  const syncPolicyAr = () => {
    if (policyEditorArRef.current && policyHiddenArRef.current) {
      const sanitized = sanitizeRichTextHtml(policyEditorArRef.current.innerHTML)
      if (policyEditorArRef.current.innerHTML !== sanitized) policyEditorArRef.current.innerHTML = sanitized
      policyHiddenArRef.current.value = sanitized
    }
  }
  const syncFooterEn = () => {
    if (footerEditorEnRef.current && footerHiddenEnRef.current) {
      const sanitized = sanitizeRichTextHtml(footerEditorEnRef.current.innerHTML)
      if (footerEditorEnRef.current.innerHTML !== sanitized) footerEditorEnRef.current.innerHTML = sanitized
      footerHiddenEnRef.current.value = sanitized
    }
  }
  const syncFooterAr = () => {
    if (footerEditorArRef.current && footerHiddenArRef.current) {
      const sanitized = sanitizeRichTextHtml(footerEditorArRef.current.innerHTML)
      if (footerEditorArRef.current.innerHTML !== sanitized) footerEditorArRef.current.innerHTML = sanitized
      footerHiddenArRef.current.value = sanitized
    }
  }

  const savedSelectionRef = useRef<Range | null>(null)
  const saveSelection = () => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange()
    }
  }
  const restoreSelection = () => {
    const sel = window.getSelection()
    if (sel && savedSelectionRef.current) {
      sel.removeAllRanges()
      sel.addRange(savedSelectionRef.current)
    }
  }

  const exec = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value)
    if (headerLocale === 'en') {
      syncEditorEn()
      editorEnRef.current?.focus()
    } else {
      syncEditorAr()
      editorArRef.current?.focus()
    }
  }

  const execPolicy = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value)
    if (policyLocale === 'en') {
      syncPolicyEn()
      policyEditorEnRef.current?.focus()
    } else {
      syncPolicyAr()
      policyEditorArRef.current?.focus()
    }
  }

  const execFooter = (cmd: string, value?: string) => {
    document.execCommand(cmd, false, value)
    if (footerLocale === 'en') {
      syncFooterEn()
      footerEditorEnRef.current?.focus()
    } else {
      syncFooterAr()
      footerEditorArRef.current?.focus()
    }
  }

  return (
    <form
      onSubmit={(e) => {
        syncEditorEn()
        syncEditorAr()
        syncPolicyEn()
        syncPolicyAr()
        syncFooterEn()
        syncFooterAr()
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'registration_design', () => setSaving(false))
      }}
      className="space-y-6"
    >
      <input
        type="hidden"
        name="event[settings]"
        value={JSON.stringify({
          registration: {
            header_placement: headerPlacement,
            footer_placement: footerPlacement,
            show_privacy_policy: showPrivacyPolicy,
          },
        })}
      />
      <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.registrationDesign}</h3>
      <p className="text-sm text-[#91918E]">{t.registrationDesignSplitHint}</p>

      <FormField label={t.registrationTopBanner} hint={t.registrationTopBannerHint}>
        <div className="flex items-center gap-3">
          {event.registrationBannerUrl && (
            <div className="relative">
              <img src={event.registrationBannerUrl} alt="" className="h-12 w-32 rounded-md border border-[#E8E8E5] object-cover object-top" />
              <button
                type="button"
                onClick={() => removeAttachment('registration_page_banner')}
                className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-[#EB5757] shadow-sm ring-1 ring-[#E8E8E5] transition-colors hover:text-[#D04040]"
                aria-label={t.confirmDelete || 'Remove image'}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Input name="event[registration_page_banner]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <FormField label={t.registrationTopBannerMobile} hint={t.registrationTopBannerMobileHint}>
        <div className="flex items-center gap-3">
          {event.registrationMobileBannerUrl && (
            <div className="relative">
              <img src={event.registrationMobileBannerUrl} alt="" className="h-12 w-24 rounded-md border border-[#E8E8E5] object-cover object-top" />
              <button
                type="button"
                onClick={() => removeAttachment('registration_page_mobile_banner')}
                className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-[#EB5757] shadow-sm ring-1 ring-[#E8E8E5] transition-colors hover:text-[#D04040]"
                aria-label={t.confirmDelete || 'Remove image'}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Input name="event[registration_page_mobile_banner]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t.registrationBackgroundDesktop} hint={t.registrationBackgroundHint}>
          <div className="flex items-center gap-3">
            {event.registrationDesktopDesignUrl && (
              <div className="relative">
                <img src={event.registrationDesktopDesignUrl} alt="" className="h-16 w-28 rounded-md border border-[#E8E8E5] object-cover" />
                <button
                  type="button"
                  onClick={() => removeAttachment('registration_page_desktop_design')}
                  className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-[#EB5757] shadow-sm ring-1 ring-[#E8E8E5] transition-colors hover:text-[#D04040]"
                  aria-label={t.confirmDelete || 'Remove image'}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <Input name="event[registration_page_desktop_design]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
          </div>
        </FormField>

        <FormField label={t.registrationBackgroundMobile} hint={t.registrationBackgroundMobileHint}>
          <div className="flex items-center gap-3">
            {event.registrationMobileDesignUrl && (
              <div className="relative">
                <img src={event.registrationMobileDesignUrl} alt="" className="h-16 w-20 rounded-md border border-[#E8E8E5] object-cover" />
                <button
                  type="button"
                  onClick={() => removeAttachment('registration_page_mobile_design')}
                  className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-[#EB5757] shadow-sm ring-1 ring-[#E8E8E5] transition-colors hover:text-[#D04040]"
                  aria-label={t.confirmDelete || 'Remove image'}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <Input name="event[registration_page_mobile_design]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
          </div>
        </FormField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {([
          { label: t.regBgColor, name: 'event[reg_bg_color]', value: bgColor, set: setBgColor },
          { label: t.regTextColor, name: 'event[reg_text_color]', value: textColor, set: setTextColor },
          { label: t.regCardBgColor, name: 'event[reg_card_bg_color]', value: cardBg, set: setCardBg },
          { label: t.regButtonColor, name: 'event[reg_button_color]', value: buttonColor, set: setButtonColor },
          { label: t.regButtonTextColor, name: 'event[reg_button_text_color]', value: buttonTextColor, set: setButtonTextColor },
          { label: t.regInputBorderColor, name: 'event[reg_input_border_color]', value: inputBorderColor, set: setInputBorderColor },
        ] as const).map(({ label, name, value, set }) => {
          const id = `cp-${name}`
          return (
            <FormField key={name} label={label}>
              <div className="flex items-center gap-2">
                <input type="hidden" name={name} value={value} />
                <input
                  id={id}
                  type="color"
                  value={value}
                  onInput={(e) => set((e.target as HTMLInputElement).value)}
                  onChange={(e) => set(e.target.value)}
                  className="sr-only"
                />
                <label
                  htmlFor={id}
                  className="h-9 w-12 shrink-0 rounded border border-[#E8E8E5] cursor-pointer transition-shadow hover:ring-2 hover:ring-[#2383E2]/30"
                  style={{ backgroundColor: value }}
                />
                <Input
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  className={formInput('flex-1')}
                  maxLength={7}
                  placeholder="#000000"
                />
              </div>
            </FormField>
          )
        })}
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <FormField label={t.regFormWidth}>
          <select
            name="event[reg_form_width]"
            value={formWidth}
            onChange={(e) => setFormWidth(e.target.value)}
            className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
          >
            <option value="narrow">{t.widthNarrow}</option>
            <option value="medium">{t.widthMedium}</option>
            <option value="wide">{t.widthWide}</option>
            <option value="full">{t.widthFull}</option>
          </select>
        </FormField>

        <FormField label={t.regFormAlignment}>
          <select
            name="event[reg_form_alignment]"
            value={formAlignment}
            onChange={(e) => setFormAlignment(e.target.value)}
            className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
          >
            <option value="left">{t.alignLeft}</option>
            <option value="center">{t.alignCenter}</option>
            <option value="right">{t.alignRight}</option>
          </select>
        </FormField>

        <FormField label={t.regMobileFormAlignment}>
          <select
            name="event[reg_mobile_form_alignment]"
            value={mobileFormAlignment}
            onChange={(e) => setMobileFormAlignment(e.target.value)}
            className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
          >
            <option value="left">{t.alignLeft}</option>
            <option value="center">{t.alignCenter}</option>
            <option value="right">{t.alignRight}</option>
          </select>
        </FormField>
      </div>

      <div className="flex items-center gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
        <input type="hidden" name="event[reg_show_lang_switcher]" value={showLangSwitcher ? '1' : '0'} />
        <Switch checked={showLangSwitcher} onCheckedChange={setShowLangSwitcher} />
        <Label className="text-sm text-[#37352F] cursor-pointer">{t.showLangSwitcher}</Label>
      </div>

      <FormField label={t.regHeaderHtml} hint={t.regHeaderHtmlHint}>
        <select
          value={headerPlacement}
          onChange={(e) => setHeaderPlacement(e.target.value as 'inside' | 'outside')}
          className="mb-2 flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
          aria-label={t.regHeaderPlacement}
        >
          <option value="inside">{t.regPlacementInside}</option>
          <option value="outside">{t.regPlacementOutside}</option>
        </select>
        <input type="hidden" name="event[reg_header_html_en]" ref={hiddenEnRef} defaultValue={event.regHeaderHtml_en || event.regHeaderHtml || defaultHeader} />
        <input type="hidden" name="event[reg_header_html_ar]" ref={hiddenArRef} defaultValue={event.regHeaderHtml_ar || event.regHeaderHtml || defaultHeader} />

        <div className="flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-[#E8E8E5] bg-[#FAFAFA] px-2 py-1.5">
          <div className="flex items-center gap-0.5 mr-2">
            {(['en', 'ar'] as const).map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => setHeaderLocale(loc)}
                className={`h-7 px-2 rounded text-xs font-medium transition-colors ${headerLocale === loc ? 'bg-[#2383E2] text-white' : 'text-[#91918E] hover:bg-[#E8E8E5]'}`}
              >
                {loc.toUpperCase()}
              </button>
            ))}
          </div>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          {([
            { cmd: 'bold', label: 'B', title: 'Bold', style: 'font-bold' },
            { cmd: 'italic', label: 'I', title: 'Italic', style: 'italic' },
            { cmd: 'underline', label: 'U', title: 'Underline', style: 'underline' },
          ] as const).map(b => (
            <button
              key={b.cmd}
              type="button"
              title={b.title}
              onClick={() => exec(b.cmd)}
              className={`h-7 w-7 rounded text-xs ${b.style} hover:bg-[#E8E8E5] transition-colors text-[#37352F]`}
            >
              {b.label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          <select
            onChange={(e) => { if (e.target.value) exec('formatBlock', e.target.value); e.target.value = '' }}
            className="h-7 rounded border-0 bg-transparent px-1 text-xs text-[#37352F] cursor-pointer hover:bg-[#E8E8E5]"
            defaultValue=""
          >
            <option value="" disabled>Heading</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="p">Paragraph</option>
          </select>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          {([
            { cmd: 'justifyLeft', title: 'Align Left' },
            { cmd: 'justifyCenter', title: 'Align Center' },
            { cmd: 'justifyRight', title: 'Align Right' },
          ] as const).map(b => (
            <button
              key={b.cmd}
              type="button"
              title={b.title}
              onClick={() => exec(b.cmd)}
              className="h-7 w-7 rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]"
            >
              {b.cmd === 'justifyLeft' ? '≡' : b.cmd === 'justifyCenter' ? '≣' : '≡'}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          <label title="Text color" className="relative cursor-pointer" onMouseDown={saveSelection}>
            <span className="flex h-7 w-7 items-center justify-center rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]">A</span>
            <input
              type="color"
              className="sr-only"
              onChange={(e) => { restoreSelection(); exec('foreColor', e.target.value) }}
            />
          </label>
        </div>
        <div
          ref={editorEnRef}
          contentEditable
          onInput={syncEditorEn}
          onBlur={syncEditorEn}
          className="min-h-[120px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_p]:leading-relaxed"
          style={{ fontFamily: event.font || undefined, display: headerLocale === 'en' ? undefined : 'none' }}
        />
        <div
          ref={editorArRef}
          contentEditable
          dir="rtl"
          onInput={syncEditorAr}
          onBlur={syncEditorAr}
          className="min-h-[120px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_p]:leading-relaxed"
          style={{ fontFamily: event.font || undefined, display: headerLocale === 'ar' ? undefined : 'none' }}
        />
      </FormField>

      <FormField label={t.privacyPolicy} hint={`${t.privacyPolicyHint} ${t.htmlSupported}`}>
        <div className="mb-3 flex items-center gap-3 rounded-md border border-[#E8E8E5] px-4 py-3">
          <Switch checked={showPrivacyPolicy} onCheckedChange={setShowPrivacyPolicy} />
          <Label className="text-sm text-[#37352F] cursor-pointer" onClick={() => setShowPrivacyPolicy((v) => !v)}>
            {t.showPrivacyPolicy}
          </Label>
        </div>
        <input type="hidden" name="event[event_terms_en]" ref={policyHiddenEnRef} defaultValue={event.eventTerms_en || event.eventTerms || ''} />
        <input type="hidden" name="event[event_terms_ar]" ref={policyHiddenArRef} defaultValue={event.eventTerms_ar || event.eventTerms || ''} />

        <div className="flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-[#E8E8E5] bg-[#FAFAFA] px-2 py-1.5">
          <div className="flex items-center gap-0.5 mr-2">
            {(['en', 'ar'] as const).map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => setPolicyLocale(loc)}
                className={`h-7 px-2 rounded text-xs font-medium transition-colors ${policyLocale === loc ? 'bg-[#2383E2] text-white' : 'text-[#91918E] hover:bg-[#E8E8E5]'}`}
              >
                {loc.toUpperCase()}
              </button>
            ))}
          </div>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          {([
            { cmd: 'bold', label: 'B', title: 'Bold', style: 'font-bold' },
            { cmd: 'italic', label: 'I', title: 'Italic', style: 'italic' },
            { cmd: 'underline', label: 'U', title: 'Underline', style: 'underline' },
          ] as const).map(b => (
            <button
              key={b.cmd}
              type="button"
              title={b.title}
              onClick={() => execPolicy(b.cmd)}
              className={`h-7 w-7 rounded text-xs ${b.style} hover:bg-[#E8E8E5] transition-colors text-[#37352F]`}
            >
              {b.label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          <select
            onChange={(e) => { if (e.target.value) execPolicy('formatBlock', e.target.value); e.target.value = '' }}
            className="h-7 rounded border-0 bg-transparent px-1 text-xs text-[#37352F] cursor-pointer hover:bg-[#E8E8E5]"
            defaultValue=""
          >
            <option value="" disabled>Heading</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="p">Paragraph</option>
          </select>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          {([
            { cmd: 'justifyLeft', title: 'Align Left' },
            { cmd: 'justifyCenter', title: 'Align Center' },
            { cmd: 'justifyRight', title: 'Align Right' },
          ] as const).map(b => (
            <button
              key={b.cmd}
              type="button"
              title={b.title}
              onClick={() => execPolicy(b.cmd)}
              className="h-7 w-7 rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]"
            >
              {b.cmd === 'justifyLeft' ? '≡' : b.cmd === 'justifyCenter' ? '≣' : '≡'}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          <label title="Text color" className="relative cursor-pointer" onMouseDown={saveSelection}>
            <span className="flex h-7 w-7 items-center justify-center rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]">A</span>
            <input
              type="color"
              className="sr-only"
              onChange={(e) => { restoreSelection(); execPolicy('foreColor', e.target.value) }}
            />
          </label>
        </div>
        <div
          ref={policyEditorEnRef}
          contentEditable
          onInput={syncPolicyEn}
          onBlur={syncPolicyEn}
          className="min-h-[160px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_a]:text-[#2383E2] [&_a]:underline [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5"
          style={{ fontFamily: event.font || undefined, display: policyLocale === 'en' ? undefined : 'none' }}
        />
        <div
          ref={policyEditorArRef}
          contentEditable
          dir="rtl"
          onInput={syncPolicyAr}
          onBlur={syncPolicyAr}
          className="min-h-[160px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_a]:text-[#2383E2] [&_a]:underline [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5"
          style={{ fontFamily: event.font || undefined, display: policyLocale === 'ar' ? undefined : 'none' }}
        />
      </FormField>

      <FormField label={t.regFooterHtml} hint={`${t.regFooterHtmlHint} ${t.htmlSupported}`}>
        <select
          value={footerPlacement}
          onChange={(e) => setFooterPlacement(e.target.value as 'inside' | 'outside')}
          className="mb-2 flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
          aria-label={t.regFooterPlacement}
        >
          <option value="inside">{t.regPlacementInside}</option>
          <option value="outside">{t.regPlacementOutside}</option>
        </select>
        <input type="hidden" name="event[reg_footer_html_en]" ref={footerHiddenEnRef} defaultValue={event.regFooterHtml_en || event.regFooterHtml || ''} />
        <input type="hidden" name="event[reg_footer_html_ar]" ref={footerHiddenArRef} defaultValue={event.regFooterHtml_ar || event.regFooterHtml || ''} />

        <div className="flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-[#E8E8E5] bg-[#FAFAFA] px-2 py-1.5">
          <div className="flex items-center gap-0.5 mr-2">
            {(['en', 'ar'] as const).map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => setFooterLocale(loc)}
                className={`h-7 px-2 rounded text-xs font-medium transition-colors ${footerLocale === loc ? 'bg-[#2383E2] text-white' : 'text-[#91918E] hover:bg-[#E8E8E5]'}`}
              >
                {loc.toUpperCase()}
              </button>
            ))}
          </div>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          {([
            { cmd: 'bold', label: 'B', title: 'Bold', style: 'font-bold' },
            { cmd: 'italic', label: 'I', title: 'Italic', style: 'italic' },
            { cmd: 'underline', label: 'U', title: 'Underline', style: 'underline' },
          ] as const).map(b => (
            <button
              key={b.cmd}
              type="button"
              title={b.title}
              onClick={() => execFooter(b.cmd)}
              className={`h-7 w-7 rounded text-xs ${b.style} hover:bg-[#E8E8E5] transition-colors text-[#37352F]`}
            >
              {b.label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          <select
            onChange={(e) => { if (e.target.value) execFooter('formatBlock', e.target.value); e.target.value = '' }}
            className="h-7 rounded border-0 bg-transparent px-1 text-xs text-[#37352F] cursor-pointer hover:bg-[#E8E8E5]"
            defaultValue=""
          >
            <option value="" disabled>Heading</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="p">Paragraph</option>
          </select>
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          {([
            { cmd: 'justifyLeft', title: 'Align Left' },
            { cmd: 'justifyCenter', title: 'Align Center' },
            { cmd: 'justifyRight', title: 'Align Right' },
          ] as const).map(b => (
            <button
              key={b.cmd}
              type="button"
              title={b.title}
              onClick={() => execFooter(b.cmd)}
              className="h-7 w-7 rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]"
            >
              {b.cmd === 'justifyLeft' ? '≡' : b.cmd === 'justifyCenter' ? '≣' : '≡'}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E8E8E5]" />
          <label title="Text color" className="relative cursor-pointer" onMouseDown={saveSelection}>
            <span className="flex h-7 w-7 items-center justify-center rounded text-xs hover:bg-[#E8E8E5] transition-colors text-[#37352F]">A</span>
            <input
              type="color"
              className="sr-only"
              onChange={(e) => { restoreSelection(); execFooter('foreColor', e.target.value) }}
            />
          </label>
        </div>
        <div
          ref={footerEditorEnRef}
          contentEditable
          onInput={syncFooterEn}
          onBlur={syncFooterEn}
          className="min-h-[120px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_a]:text-[#2383E2] [&_a]:underline [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5"
          style={{ fontFamily: event.font || undefined, display: footerLocale === 'en' ? undefined : 'none' }}
        />
        <div
          ref={footerEditorArRef}
          contentEditable
          dir="rtl"
          onInput={syncFooterAr}
          onBlur={syncFooterAr}
          className="min-h-[120px] w-full rounded-b-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors [&_a]:text-[#2383E2] [&_a]:underline [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5"
          style={{ fontFamily: event.font || undefined, display: footerLocale === 'ar' ? undefined : 'none' }}
        />
      </FormField>

      <FormField label={t.customCss} hint={t.customCssHint}>
        <textarea
          name="event[reg_custom_css]"
          value={customCss}
          onChange={(e) => setCustomCss(e.target.value)}
          rows={6}
          spellCheck={false}
          className="w-full rounded-md border border-[#E8E8E5] bg-[#FAFAFA] px-3 py-2 text-sm text-[#37352F] font-mono placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
          placeholder={`.ev-reg-card {\n  border-radius: 16px;\n}`}
        />
      </FormField>

        <div className="rounded-md border border-[#E8E8E5] bg-[#FAFAFA] p-4 text-xs text-[#6B6B6B] space-y-3 max-h-96 overflow-y-auto">
        <h4 className="font-semibold text-[#37352F] text-sm">Available CSS Classes</h4>
        <div className="grid gap-1.5 sm:grid-cols-2 font-mono text-[11px]">
          <div><span className="text-[#2383E2]">.ev-reg-page</span> — page wrapper</div>
          <div><span className="text-[#2383E2]">.ev-reg-landing-page</span> — landing step</div>
          <div><span className="text-[#2383E2]">.ev-reg-form-page</span> — form step</div>
          <div><span className="text-[#2383E2]">.ev-reg-link-media</span> — one registration link (use its slug)</div>
          <div><span className="text-[#2383E2]">.ev-reg-agenda-page</span> — sessions step</div>
          <div><span className="text-[#2383E2]">.ev-reg-confirmation-page</span> — confirmation</div>
          <div><span className="text-[#2383E2]">.ev-reg-shell</span> — centered content column</div>
          <div><span className="text-[#2383E2]">.ev-reg-header</span> — HTML header block</div>
          <div><span className="text-[#2383E2]">.ev-reg-footer</span> — HTML footer block</div>
          <div><span className="text-[#2383E2]">.ev-reg-card</span> — white card container</div>
          <div><span className="text-[#2383E2]">.ev-reg-landing-card</span> — landing card</div>
          <div><span className="text-[#2383E2]">.ev-reg-steps</span> — wizard step bar</div>
          <div><span className="text-[#2383E2]">.ev-reg-step-active</span> — current step</div>
          <div><span className="text-[#2383E2]">.ev-reg-new-registration-button</span> — primary CTA</div>
          <div><span className="text-[#2383E2]">.ev-reg-lookup-form</span> — returnee form</div>
          <div><span className="text-[#2383E2]">.ev-reg-divider</span> — “or” separator</div>
          <div><span className="text-[#2383E2]">.ev-reg-lang-switcher</span> — language toggle</div>
          <div><span className="text-[#2383E2]">.ev-reg-logo-wrap</span> — logo above card</div>
          <div><span className="text-[#2383E2]">.ev-reg-top-banner</span> — header banner image</div>
          <div><span className="text-[#2383E2]">.ev-reg-page-body</span> — area below banner</div>
          <div><span className="text-[#2383E2]">.ev-reg-split-layout</span> — banner + background mode</div>
          <div><span className="text-[#2383E2]">.ev-reg-background-layout</span> — full-page background when no banner</div>
          <div><span className="text-[#2383E2]">.ev-reg-background</span> — page background behind the form</div>
          <div><span className="text-[#2383E2]">.ev-reg-button</span> — primary buttons</div>
          <div><span className="text-[#2383E2]">.ev-reg-button-secondary</span> — outline buttons</div>
          <div><span className="text-[#2383E2]">.ev-reg-input</span> — text inputs</div>
        </div>
        <p className="pt-1.5 border-t border-[#E8E8E5]">
          CSS variables on <code className="text-[#2383E2]">.ev-reg-page</code>:
          <code className="text-[#2383E2]"> --ev-primary</code>,
          <code className="text-[#2383E2]"> --ev-bg</code>,
          <code className="text-[#2383E2]"> --ev-text</code>,
          <code className="text-[#2383E2]"> --ev-card-bg</code>,
          <code className="text-[#2383E2]"> --ev-btn</code>,
          <code className="text-[#2383E2]"> --ev-input-border</code>
        </p>
        <details className="pt-1 border-t border-[#E8E8E5]">
          <summary className="cursor-pointer font-medium text-[#37352F]">Example: forum-style landing card</summary>
          <pre className="mt-2 whitespace-pre-wrap rounded bg-white p-3 text-[10px] leading-relaxed text-[#37352F]">{`.ev-reg-landing-page {
  --ev-bg: #1f6b66;
  --ev-primary: #1f6b66;
}
.ev-reg-landing-page .ev-reg-header {
  color: #fff;
  text-align: center;
}
.ev-reg-landing-page .ev-reg-header h1 {
  color: #fff;
  font-size: 1.75rem;
}
.ev-reg-landing-page .ev-reg-shell {
  max-width: 560px;
}
.ev-reg-landing-page .ev-reg-landing-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0,0,0,.2);
  padding: 2rem 2.5rem;
}
.ev-reg-landing-page .ev-reg-footer {
  color: rgba(255,255,255,.9);
  text-align: center;
}
.ev-reg-landing-page .ev-reg-lookup-form {
  background: #f3faf8;
}`}</pre>
        </details>
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.previewColors}</h4>
        <div className="rounded-lg border border-[#E8E8E5] p-6" style={{ backgroundColor: bgColor }}>
          <div className="mx-auto max-w-xs rounded-lg p-4" style={{ backgroundColor: cardBg }}>
            <p className="mb-3 text-sm font-medium" style={{ color: textColor }}>Sample Field Label</p>
            <div
              className="mb-4 h-9 rounded-md border px-3 py-1.5 text-sm"
              style={{ borderColor: inputBorderColor, color: textColor, backgroundColor: cardBg }}
            >
              Placeholder text...
            </div>
            <button
              type="button"
              className="w-full rounded-md px-4 py-2 text-sm font-semibold"
              style={{ backgroundColor: buttonColor, color: buttonTextColor }}
            >
              Register
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Condition Builder Modal                                            */
/* ------------------------------------------------------------------ */

const OPERATORS = [
  { value: 'equals', key: 'equals' },
  { value: 'not_equals', key: 'notEquals' },
  { value: 'contains', key: 'contains' },
  { value: 'not_contains', key: 'notContains' },
  { value: 'is_empty', key: 'isEmpty' },
  { value: 'is_not_empty', key: 'isNotEmpty' },
  { value: 'is_one_of', key: 'isOneOf' },
] as const

const ACTIONS = [
  { value: 'show', key: 'actionShow' },
  { value: 'hide', key: 'actionHide' },
  { value: 'require', key: 'actionRequire' },
  { value: 'unrequire', key: 'actionUnrequire' },
] as const

function normalizeConditions(value: unknown): FieldCondition[] {
  if (Array.isArray(value)) return value as FieldCondition[]
  if (typeof value !== 'string') return []

  const trimmedValue = value.trim()
  if (!trimmedValue) return []

  try {
    return normalizeConditions(JSON.parse(trimmedValue))
  } catch {
    return []
  }
}

function ConditionBuilder({
  conditions: _conditions,
  onChange,
  standardFields,
  customFields,
  currentFieldKey,
  currentFieldType,
  t,
}: {
  conditions: FieldCondition[] | string | null | undefined
  onChange: (conditions: FieldCondition[]) => void
  standardFields: { field: string }[]
  customFields: { id: number | null; name: string; fieldType: string }[]
  currentFieldKey: string
  currentFieldType: 'standard' | 'custom'
  t: Record<string, string>
}) {
  const conditions = Array.isArray(_conditions) ? _conditions : []
  const [open, setOpen] = useState(false)
  const normalizedConditions = normalizeConditions(conditions)

  /* All available source fields except the current one */
  const sourceOptions = [
    ...standardFields
      .filter((f) => !(currentFieldType === 'standard' && f.field === currentFieldKey))
      .map((f) => ({ label: f.field, value: f.field, id: null, type: 'standard' as const })),
    ...customFields
      .filter((cf) => !(currentFieldType === 'custom' && cf.name === currentFieldKey))
      .map((cf) => ({ label: `${cf.name} (${t.customField})`, value: cf.name, id: cf.id, type: 'custom' as const })),
  ]

  function addCondition() {
    const first = sourceOptions[0]
    if (!first) return
    onChange([
      ...normalizedConditions,
      {
        id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        sourceField: first.value,
        sourceFieldId: first.id,
        sourceType: first.type,
        operator: 'equals',
        value: '',
        action: 'show',
      },
    ])
  }

  function updateCondition(id: string, updates: Partial<FieldCondition>) {
    onChange(normalizedConditions.map((c) => (c.id === id ? { ...c, ...updates } : c)))
  }

  function removeCondition(id: string) {
    onChange(normalizedConditions.filter((c) => c.id !== id))
  }

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors cursor-pointer ${
          normalizedConditions.length > 0
            ? 'bg-[#EBF5FF] text-[#2383E2]'
            : 'text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]'
        }`}
        title={t.conditions}
      >
        <GitBranch className="h-3 w-3" />
        {normalizedConditions.length > 0 && <span>{normalizedConditions.length}</span>}
      </button>

      {/* Modal backdrop + dialog */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-xl rounded-lg border border-[#E8E8E5] bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#37352F]">
                {t.conditions}: <span className="font-normal text-[#91918E]">{currentFieldKey}</span>
              </h3>
              <button type="button" onClick={() => setOpen(false)} className="text-[#91918E] hover:text-[#37352F] cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            {normalizedConditions.length === 0 ? (
              <p className="text-sm text-[#91918E] text-center py-4">{t.noConditions}</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {normalizedConditions.map((cond) => (
                  <div key={cond.id} className="rounded-md border border-[#E8E8E5] p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      {/* Source field */}
                      <div className="flex-1 space-y-1">
                        <label className="text-[10px] text-[#91918E] uppercase">{t.sourceField}</label>
                        <select
                          value={cond.sourceField}
                          onChange={(e) => {
                            const opt = sourceOptions.find((o) => o.value === e.target.value)
                            updateCondition(cond.id, {
                              sourceField: e.target.value,
                              sourceFieldId: opt?.id ?? null,
                              sourceType: opt?.type || 'standard',
                            })
                          }}
                          className="w-full h-7 rounded border border-[#E8E8E5] px-1.5 text-xs text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                        >
                          {sourceOptions.map((opt) => (
                            <option key={`${opt.type}-${opt.value}`} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Operator */}
                      <div className="w-36 shrink-0 space-y-1">
                        <label className="text-[10px] text-[#91918E] uppercase">{t.operator}</label>
                        <select
                          value={cond.operator}
                          onChange={(e) => updateCondition(cond.id, { operator: e.target.value as FieldCondition['operator'] })}
                          className="w-full h-7 rounded border border-[#E8E8E5] px-1.5 text-xs text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                        >
                          {OPERATORS.map((op) => (
                            <option key={op.value} value={op.value}>{t[op.key]}</option>
                          ))}
                        </select>
                      </div>

                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => removeCondition(cond.id)}
                        className="mt-4 text-[#EB5757] hover:text-[#D04040] cursor-pointer p-0.5 shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Value (hidden for is_empty / is_not_empty) */}
                      {cond.operator !== 'is_empty' && cond.operator !== 'is_not_empty' && (
                        <div className="flex-1 space-y-1">
                          <label className="text-[10px] text-[#91918E] uppercase">{t.value}</label>
                          <Input
                            value={cond.value}
                            onChange={(e) => updateCondition(cond.id, { value: e.target.value })}
                            placeholder={cond.operator === 'is_one_of' ? 'Value1, Value2, Value3' : t.value}
                            className="h-7 text-xs"
                          />
                        </div>
                      )}

                      {/* Action */}
                      <div className={`${cond.operator === 'is_empty' || cond.operator === 'is_not_empty' ? 'flex-1' : 'w-44 shrink-0'} space-y-1`}>
                        <label className="text-[10px] text-[#91918E] uppercase">{t.action}</label>
                        <select
                          value={cond.action}
                          onChange={(e) => updateCondition(cond.id, { action: e.target.value as FieldCondition['action'] })}
                          className="w-full h-7 rounded border border-[#E8E8E5] px-1.5 text-xs text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                        >
                          {ACTIONS.map((a) => (
                            <option key={a.value} value={a.value}>{t[a.key]}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F1F1EF]">
              <button
                type="button"
                onClick={addCondition}
                disabled={sourceOptions.length === 0}
                className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" />
                {t.addCondition}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-1.5 text-xs font-medium bg-[#2383E2] text-white hover:bg-[#1B6FC0] transition-colors cursor-pointer"
              >
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

type RadioOptionRow = {
  en: string
  ar: string
  iconUrl: string | null
  blobId: string
  iconRev?: number
}

function zipRadioOptionRows(
  enText: string,
  arText: string,
  urls: Array<string | null | undefined> = [],
  blobIds: Array<string | null | undefined> = [],
): RadioOptionRow[] {
  const en = enText.split(/\r?\n/)
  const ar = arText.split(/\r?\n/)
  const n = Math.max(en.length, ar.length, urls.length, blobIds.length, 1)
  return Array.from({ length: n }, (_, i) => ({
    en: en[i] ?? '',
    ar: ar[i] ?? '',
    iconUrl: urls[i] || null,
    blobId: blobIds[i] || '',
    iconRev: 0,
  }))
}

function joinRadioOptionLines(rows: RadioOptionRow[], loc: 'en' | 'ar') {
  return rows.map((row) => (loc === 'en' ? row.en : row.ar)).join('\n')
}

/* ------------------------------------------------------------------ */
/*  Section: Registration                                              */
/* ------------------------------------------------------------------ */

function RegistrationSection({
  event,
  registrationFields,
  customFields: initialCustomFields,
  urls,
  t,
}: {
  event: EventData
  registrationFields: RegistrationFieldItem[]
  customFields: CustomFieldItem[]
  urls: SetupProps['urls']
  t: Record<string, string>
}) {
  const [saving, setSaving] = useState(false)
  const [enabledFields, setEnabledFields] = useState<Set<string>>(
    new Set(registrationFields.filter((f) => f.active && !f.custom).map((f) => f.field))
  )
  const [fieldOrders, setFieldOrders] = useState<Record<string, number>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f, i) => [f.field, f.order || i + 1]))
  )
  const [fieldWidths, setFieldWidths] = useState<Record<string, number>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f) => [f.field, f.width || 100]))
  )
  const [fieldRequired, setFieldRequired] = useState<Record<string, boolean>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f) => [f.field, f.required || false]))
  )
  const [fieldUnique, setFieldUnique] = useState<Record<string, boolean>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f) => [f.field, f.uniquePerEvent || false]))
  )
  const [fieldStartNewRow, setFieldStartNewRow] = useState<Record<string, boolean>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f) => [f.field, f.startNewRow || false]))
  )
  const [fieldHidden, setFieldHidden] = useState<Record<string, boolean>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f) => [f.field, f.hidden || false]))
  )
  const [fieldConditions, setFieldConditions] = useState<Record<string, FieldCondition[]>>(
    Object.fromEntries(registrationFields.filter((f) => !f.custom).map((f) => [f.field, normalizeConditions(f.conditions)]))
  )
  const [customFieldsList, setCustomFieldsList] = useState<Array<{
    key: number
    id: number | null
    name: string
    name_en: string
    name_ar: string
    fieldType: string
    fieldOptions: string
    fieldOptions_en: string
    fieldOptions_ar: string
    optionRows: RadioOptionRow[]
    order: number
    required: boolean
    width: number
    uniquePerEvent: boolean
    startNewRow: boolean
    showChoiceStats: boolean
    hidden: boolean
    conditions: FieldCondition[]
    _destroy: boolean
  }>>(
    initialCustomFields.map((cf, i) => ({
      key: i,
      id: cf.id,
      name: cf.name,
      name_en: cf.name_en || cf.name,
      name_ar: cf.name_ar || cf.name,
      fieldType: cf.fieldType,
      fieldOptions: cf.fieldOptions,
      fieldOptions_en: cf.fieldOptions_en || cf.fieldOptions,
      fieldOptions_ar: cf.fieldOptions_ar || cf.fieldOptions,
      optionRows: zipRadioOptionRows(
        cf.fieldOptions_en || cf.fieldOptions,
        cf.fieldOptions_ar || cf.fieldOptions,
        cf.optionIconUrls,
        cf.optionIconBlobIds,
      ),
      order: cf.order,
      required: cf.required,
      width: cf.width || 100,
      uniquePerEvent: cf.uniquePerEvent || false,
      startNewRow: cf.startNewRow || false,
      showChoiceStats: cf.showChoiceStats || false,
      hidden: cf.hidden || false,
      conditions: normalizeConditions(cf.conditions),
      _destroy: false,
    }))
  )
  const [nextKey, setNextKey] = useState(initialCustomFields.length)

  const standardFields = registrationFields.filter((f) => !f.custom)

  function toggleField(field: string) {
    setEnabledFields((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  function setOrder(field: string, order: number) {
    setFieldOrders((prev) => ({ ...prev, [field]: order }))
  }

  function addCustomField() {
    setCustomFieldsList((prev) => [
      ...prev,
      { key: nextKey, id: null, name: '', name_en: '', name_ar: '', fieldType: 'text_field', fieldOptions: '', fieldOptions_en: '', fieldOptions_ar: '', optionRows: [{ en: '', ar: '', iconUrl: null, blobId: '' }], order: prev.length + 1, required: false, width: 100, uniquePerEvent: false, startNewRow: false, showChoiceStats: false, hidden: false, conditions: [], _destroy: false },
    ])
    setNextKey((k) => k + 1)
  }

  function updateCustomField(key: number, updates: Partial<typeof customFieldsList[0]>) {
    setCustomFieldsList((prev) =>
      prev.map((cf) => (cf.key === key ? { ...cf, ...updates } : cf))
    )
  }

  function updateRadioOptionRows(key: number, rows: RadioOptionRow[]) {
    updateCustomField(key, {
      optionRows: rows,
      fieldOptions_en: joinRadioOptionLines(rows, 'en'),
      fieldOptions_ar: joinRadioOptionLines(rows, 'ar'),
      fieldOptions: joinRadioOptionLines(rows, 'en'),
    })
  }

  function removeCustomField(key: number) {
    setCustomFieldsList((prev) =>
      prev.map((cf) => (cf.key === key ? { ...cf, _destroy: true } : cf))
    )
  }

  function removeAttachment(field: 'registration_page_banner' | 'registration_page_footer_image') {
    if (!confirm(t.confirmDelete || 'Are you sure?')) return
    router.delete(urls.deleteEventImageAttachment, {
      data: { [field]: '1' },
      preserveScroll: true,
      preserveState: true,
    })
  }

  return (
    <form
      onSubmit={(e) => {
        setSaving(true)
        submitSetupForm(e, urls.updateEvent, 'registration', () => setSaving(false))
      }}
      className="space-y-6"
    >
      {/* Banner */}
      <FormField label={t.registrationBanner}>
        <div className="flex items-center gap-3">
          {event.registrationBannerUrl && (
            <div className="relative">
              <img src={event.registrationBannerUrl} alt="" className="h-12 w-24 rounded-md border border-[#E8E8E5] object-cover" />
              <button
                type="button"
                onClick={() => removeAttachment('registration_page_banner')}
                className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-[#EB5757] shadow-sm ring-1 ring-[#E8E8E5] transition-colors hover:text-[#D04040]"
                aria-label={t.confirmDelete || 'Remove image'}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Input name="event[registration_page_banner]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      <FormField label={t.registrationFooterImage}>
        <div className="flex items-center gap-3">
          {event.registrationFooterImageUrl && (
            <div className="relative">
              <img src={event.registrationFooterImageUrl} alt="" className="h-12 w-24 rounded-md border border-[#E8E8E5] object-cover" />
              <button
                type="button"
                onClick={() => removeAttachment('registration_page_footer_image')}
                className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-[#EB5757] shadow-sm ring-1 ring-[#E8E8E5] transition-colors hover:text-[#D04040]"
                aria-label={t.confirmDelete || 'Remove image'}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Input name="event[registration_page_footer_image]" type="file" accept="image/jpeg,image/png" className={formInput('flex-1')} />
        </div>
      </FormField>

      {/* Seating plan */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t.seatRows}>
          <Input name="event[seat_rows]" type="number" defaultValue={event.seatRows ?? ''} className={formInput()} />
        </FormField>
        <FormField label={t.seatColumns}>
          <Input name="event[seat_columns]" type="number" defaultValue={event.seatColumns ?? ''} className={formInput()} />
        </FormField>
      </div>

      {/* ─── Standard Registration Fields ─── */}
      <div className="space-y-3">
        <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.registrationFieldsLabel}</h3>
        <div className="rounded-md border border-[#E8E8E5] divide-y divide-[#F1F1EF]">
          {standardFields.map((rf, idx) => (
            <div key={rf.field} className="flex items-center gap-3 px-4 py-2">
              <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
                <input
                  type="checkbox"
                  name="event[registration_fields][]"
                  value={rf.field}
                  checked={enabledFields.has(rf.field)}
                  onChange={() => toggleField(rf.field)}
                  className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2] shrink-0"
                />
                <span className="text-sm text-[#37352F] truncate">{rf.field}</span>
              </label>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-[#91918E]">{t.order}:</span>
                <input
                  type="number"
                  name={`event[event_registration_fields_attributes][${idx}][order]`}
                  value={fieldOrders[rf.field] ?? idx + 1}
                  onChange={(e) => setOrder(rf.field, parseInt(e.target.value) || 0)}
                  className="w-14 h-7 rounded border border-[#E8E8E5] px-2 text-xs text-[#37352F] text-center focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                />
                <select
                  name={`event[event_registration_fields_attributes][${idx}][width]`}
                  value={fieldWidths[rf.field] ?? 100}
                  onChange={(e) => setFieldWidths(prev => ({ ...prev, [rf.field]: parseInt(e.target.value) }))}
                  className="h-7 rounded border border-[#E8E8E5] px-1 text-xs text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]"
                >
                  <option value={33}>33%</option>
                  <option value={50}>50%</option>
                  <option value={100}>100%</option>
                </select>
                <label className="flex items-center gap-1 cursor-pointer" title={t.startOnNewRowHint || t.startOnNewRow}>
                  <input type="hidden" name={`event[event_registration_fields_attributes][${idx}][start_new_row]`} value="0" />
                  <input
                    type="checkbox"
                    name={`event[event_registration_fields_attributes][${idx}][start_new_row]`}
                    value="1"
                    checked={fieldStartNewRow[rf.field] || false}
                    onChange={(e) => setFieldStartNewRow(prev => ({ ...prev, [rf.field]: e.target.checked }))}
                    className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                  />
                  <span className="text-[10px] text-[#91918E]">{t.startOnNewRow}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer" title={t.required}>
                  <input
                    type="checkbox"
                    name={`event[event_registration_fields_attributes][${idx}][required]`}
                    value="1"
                    checked={fieldRequired[rf.field] || false}
                    onChange={(e) => setFieldRequired(prev => ({ ...prev, [rf.field]: e.target.checked }))}
                    className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                  />
                  <span className="text-[10px] text-[#91918E]">{t.required}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer" title={t.uniquePerEvent}>
                  <input
                    type="checkbox"
                    name={`event[event_registration_fields_attributes][${idx}][unique_per_event]`}
                    value="1"
                    checked={fieldUnique[rf.field] || false}
                    onChange={(e) => setFieldUnique(prev => ({ ...prev, [rf.field]: e.target.checked }))}
                    className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                  />
                  <span className="text-[10px] text-[#91918E]">{t.uniquePerEvent}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer" title={t.hidden}>
                  <input
                    type="checkbox"
                    checked={fieldHidden[rf.field] || false}
                    onChange={(e) => setFieldHidden(prev => ({ ...prev, [rf.field]: e.target.checked }))}
                    className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#91918E]"
                  />
                  <EyeOff className="h-3 w-3 text-[#91918E]" />
                </label>
                <ConditionBuilder
                  conditions={fieldConditions[rf.field] || []}
                  onChange={(conds) => setFieldConditions(prev => ({ ...prev, [rf.field]: conds }))}
                  standardFields={standardFields.filter(f => enabledFields.has(f.field))}
                  customFields={customFieldsList.filter(cf => !cf._destroy).map(cf => ({ id: cf.id, name: cf.name_en || cf.name, fieldType: cf.fieldType }))}
                  currentFieldKey={rf.field}
                  currentFieldType="standard"
                  t={t}
                />
                <input type="hidden" name={`event[event_registration_fields_attributes][${idx}][field]`} value={rf.field} />
                <input type="hidden" name={`event[event_registration_fields_attributes][${idx}][hidden]`} value={fieldHidden[rf.field] ? '1' : '0'} />
                <input type="hidden" name={`event[event_registration_fields_attributes][${idx}][conditions]`} value={JSON.stringify(fieldConditions[rf.field] || [])} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Custom Fields ─── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.customFieldsLabel}</h3>
          <button
            type="button"
            onClick={addCustomField}
            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            {t.addFields}
          </button>
        </div>

        {customFieldsList.filter((cf) => !cf._destroy).length === 0 ? (
          <div className="rounded-md border border-dashed border-[#E8E8E5] px-4 py-6 text-center">
            <ClipboardList className="h-8 w-8 text-[#D3D3D0] mx-auto mb-2" />
            <p className="text-sm text-[#91918E]">{t.noCustomFields}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {customFieldsList.map((cf, idx) => {
              if (cf._destroy) {
                // Emit a hidden _destroy field for existing records
                return cf.id ? (
                  <div key={cf.key}>
                    <input type="hidden" name={`event[fields_attributes][${idx}][id]`} value={cf.id} />
                    <input type="hidden" name={`event[fields_attributes][${idx}][_destroy]`} value="1" />
                  </div>
                ) : null
              }
              return (
                <div key={cf.key} className="rounded-lg border border-[#E8E8E5] p-4 space-y-3">
                  {cf.id && <input type="hidden" name={`event[fields_attributes][${idx}][id]`} value={cf.id} />}

                  <div className="flex items-start gap-3">
                    {/* Field type */}
                    <div className="space-y-1 w-36 shrink-0">
                      <Label className="text-xs text-[#91918E]">{t.fieldType}</Label>
                      <select
                        name={`event[fields_attributes][${idx}][field_type]`}
                        value={cf.fieldType}
                        onChange={(e) => {
                          const fieldType = e.target.value
                          if (fieldType === 'radio_button') {
                            updateCustomField(cf.key, {
                              fieldType,
                              optionRows: zipRadioOptionRows(cf.fieldOptions_en, cf.fieldOptions_ar, cf.optionRows?.map((row) => row.iconUrl), cf.optionRows?.map((row) => row.blobId)),
                            })
                          } else {
                            updateCustomField(cf.key, { fieldType })
                          }
                        }}
                        className={formInput('w-full')}
                      >
                        <option value="text_field">{t.textField}</option>
                        <option value="text_area">{t.textArea}</option>
                        <option value="check_box">{t.checkBox}</option>
                        <option value="drop_down">{t.dropDown}</option>
                        <option value="radio_button">{t.radioButton}</option>
                      </select>
                    </div>

                    {/* Name (locale tabs) */}
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs text-[#91918E]">{t.fieldName}</Label>
                      <div className="space-y-1.5">
                        {(['en', 'ar'] as const).map(loc => (
                          <div key={loc} className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-[#91918E] uppercase w-5 shrink-0">{loc}</span>
                            <Input
                              name={`event[fields_attributes][${idx}][name_${loc}]`}
                              value={loc === 'en' ? cf.name_en : cf.name_ar}
                              onChange={(e) => updateCustomField(cf.key, { [`name_${loc}`]: e.target.value, ...(loc === 'en' ? { name: e.target.value } : {}) })}
                              placeholder={`${t.fieldName} (${loc.toUpperCase()})`}
                              className={formInput('flex-1')}
                              dir={loc === 'ar' ? 'rtl' : undefined}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order */}
                    <div className="space-y-1 w-20 shrink-0">
                      <Label className="text-xs text-[#91918E]">{t.order}</Label>
                      <Input
                        name={`event[fields_attributes][${idx}][order]`}
                        type="number"
                        value={cf.order}
                        onChange={(e) => updateCustomField(cf.key, { order: parseInt(e.target.value) || 0 })}
                        className={formInput()}
                      />
                    </div>

                    {/* Width */}
                    <div className="space-y-1 w-20 shrink-0">
                      <Label className="text-xs text-[#91918E]">{t.fieldWidth}</Label>
                      <select
                        name={`event[fields_attributes][${idx}][width]`}
                        value={cf.width}
                        onChange={(e) => updateCustomField(cf.key, { width: parseInt(e.target.value) })}
                        className={formInput('w-full')}
                      >
                        <option value={33}>33%</option>
                        <option value={50}>50%</option>
                        <option value={100}>100%</option>
                      </select>
                    </div>

                    {/* Start on new row */}
                    <div className="flex flex-col items-center gap-1.5 pt-5 shrink-0">
                      <label className="flex items-center gap-1.5 cursor-pointer" title={t.startOnNewRowHint || t.startOnNewRow}>
                        <input type="hidden" name={`event[fields_attributes][${idx}][start_new_row]`} value="0" />
                        <input
                          type="checkbox"
                          name={`event[fields_attributes][${idx}][start_new_row]`}
                          value="1"
                          checked={cf.startNewRow}
                          onChange={(e) => updateCustomField(cf.key, { startNewRow: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                        />
                        <span className="text-xs text-[#91918E]">{t.startOnNewRow}</span>
                      </label>
                    </div>

                    {/* Required + Unique + Hidden + Conditions + Remove */}
                    <div className="flex flex-col items-center gap-1.5 pt-5 shrink-0">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="hidden" name={`event[fields_attributes][${idx}][required]`} value="0" />
                        <input
                          type="checkbox"
                          name={`event[fields_attributes][${idx}][required]`}
                          value="1"
                          checked={cf.required}
                          onChange={(e) => updateCustomField(cf.key, { required: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                        />
                        <span className="text-xs text-[#91918E]">{t.required}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="hidden" name={`event[fields_attributes][${idx}][unique_per_event]`} value="0" />
                        <input
                          type="checkbox"
                          name={`event[fields_attributes][${idx}][unique_per_event]`}
                          value="1"
                          checked={cf.uniquePerEvent}
                          onChange={(e) => updateCustomField(cf.key, { uniquePerEvent: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                        />
                        <span className="text-xs text-[#91918E]">{t.uniquePerEvent}</span>
                      </label>
                      {(cf.fieldType === 'drop_down' || cf.fieldType === 'radio_button') ? (
                        <label className="flex items-center gap-1.5 cursor-pointer" title={t.showChoiceStatsHint || t.showChoiceStats}>
                          <input type="hidden" name={`event[fields_attributes][${idx}][show_choice_stats]`} value="0" />
                          <input
                            type="checkbox"
                            name={`event[fields_attributes][${idx}][show_choice_stats]`}
                            value="1"
                            checked={cf.showChoiceStats}
                            onChange={(e) => updateCustomField(cf.key, { showChoiceStats: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#2383E2]"
                          />
                          <span className="text-xs text-[#91918E]">{t.showChoiceStats || 'Show results'}</span>
                        </label>
                      ) : (
                        <input type="hidden" name={`event[fields_attributes][${idx}][show_choice_stats]`} value="0" />
                      )}
                      <label className="flex items-center gap-1.5 cursor-pointer" title={t.hidden}>
                        <input
                          type="checkbox"
                          checked={cf.hidden}
                          onChange={(e) => updateCustomField(cf.key, { hidden: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-[#E8E8E5] text-[#91918E]"
                        />
                        <EyeOff className="h-3 w-3 text-[#91918E]" />
                      </label>
                      <ConditionBuilder
                        conditions={cf.conditions}
                        onChange={(conds) => updateCustomField(cf.key, { conditions: conds })}
                        standardFields={standardFields.filter(f => enabledFields.has(f.field))}
                        customFields={customFieldsList.filter(c => !c._destroy && c.key !== cf.key).map(c => ({ id: c.id, name: c.name_en || c.name, fieldType: c.fieldType }))}
                        currentFieldKey={cf.name_en || cf.name}
                        currentFieldType="custom"
                        t={t}
                      />
                      <button
                        type="button"
                        onClick={() => removeCustomField(cf.key)}
                        className="text-[#EB5757] hover:text-[#D04040] cursor-pointer p-0.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <input type="hidden" name={`event[fields_attributes][${idx}][hidden]`} value={cf.hidden ? '1' : '0'} />
                      <input type="hidden" name={`event[fields_attributes][${idx}][conditions]`} value={JSON.stringify(cf.conditions)} />
                    </div>
                  </div>

                  {/* Options for dropdown / radio button types */}
                  {cf.fieldType === 'radio_button' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-[#91918E]">{t.radioOptions || t.dropdownOptions}</Label>
                      <p className="text-[11px] text-[#C4C4C0]">{t.radioOptionIconHint}</p>
                      <div className="space-y-2">
                        {(cf.optionRows || []).map((row, optionIdx) => (
                          <div key={optionIdx} className="flex flex-wrap items-center gap-2 rounded-md border border-[#E8E8E5] bg-[#FBFBFB] p-2">
                            <input type="hidden" name={`event[fields_attributes][${idx}][option_icon_keep_ids][${optionIdx}]`} value={row.blobId} />
                            <div className="flex items-center gap-2">
                              {row.iconUrl ? (
                                <img src={row.iconUrl} alt="" className="h-8 w-8 rounded object-contain border border-[#E8E8E5] bg-white" />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-[#E8E8E5] bg-white text-[#C4C4C0]">
                                  <ImagePlus className="h-3.5 w-3.5" />
                                </div>
                              )}
                              <label className="cursor-pointer text-[11px] font-medium text-[#2383E2] hover:underline">
                                {t.radioOptionIcon || 'Icon'}
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  name={`event[fields_attributes][${idx}][option_icon_files][${optionIdx}]`}
                                  key={`${cf.key}-${optionIdx}-${row.iconRev || 0}`}
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0]
                                    const next = [...(cf.optionRows || [])]
                                    next[optionIdx] = {
                                      ...row,
                                      iconUrl: file ? URL.createObjectURL(file) : row.iconUrl,
                                    }
                                    updateRadioOptionRows(cf.key, next)
                                  }}
                                />
                              </label>
                              {row.iconUrl && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = [...(cf.optionRows || [])]
                                    next[optionIdx] = { ...row, iconUrl: null, blobId: '', iconRev: (row.iconRev || 0) + 1 }
                                    updateRadioOptionRows(cf.key, next)
                                  }}
                                  className="text-[11px] text-[#EB5757] hover:underline"
                                >
                                  {t.delete || 'Remove'}
                                </button>
                              )}
                            </div>
                            <Input
                              value={row.en}
                              onChange={(e) => {
                                const next = [...(cf.optionRows || [])]
                                next[optionIdx] = { ...row, en: e.target.value }
                                updateRadioOptionRows(cf.key, next)
                              }}
                              placeholder={`${t.dropdownOptions} (EN)`}
                              className={formInput('flex-1 min-w-[8rem]')}
                            />
                            <Input
                              value={row.ar}
                              onChange={(e) => {
                                const next = [...(cf.optionRows || [])]
                                next[optionIdx] = { ...row, ar: e.target.value }
                                updateRadioOptionRows(cf.key, next)
                              }}
                              placeholder={`${t.dropdownOptions} (AR)`}
                              dir="rtl"
                              className={formInput('flex-1 min-w-[8rem]')}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const next = (cf.optionRows || []).filter((_, i) => i !== optionIdx)
                                updateRadioOptionRows(cf.key, next.length ? next : [{ en: '', ar: '', iconUrl: null, blobId: '' }])
                              }}
                              className="rounded-md p-1.5 text-[#EB5757] hover:bg-[#FBE4E4]"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <textarea name={`event[fields_attributes][${idx}][field_options_en]`} value={cf.fieldOptions_en} readOnly hidden />
                      <textarea name={`event[fields_attributes][${idx}][field_options_ar]`} value={cf.fieldOptions_ar} readOnly hidden />
                      <button
                        type="button"
                        onClick={() => updateRadioOptionRows(cf.key, [...(cf.optionRows || []), { en: '', ar: '', iconUrl: null, blobId: '' }])}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF]"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t.addOption || 'Add option'}
                      </button>
                    </div>
                  )}
                  {cf.fieldType === 'drop_down' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-[#91918E]">{t.dropdownOptions}</Label>
                      <div className="space-y-1.5">
                        {(['en', 'ar'] as const).map(loc => (
                          <div key={loc} className="flex items-start gap-1.5">
                            <span className="text-[10px] font-medium text-[#91918E] uppercase w-5 shrink-0 pt-2">{loc}</span>
                            <textarea
                              name={`event[fields_attributes][${idx}][field_options_${loc}]`}
                              value={loc === 'en' ? cf.fieldOptions_en : cf.fieldOptions_ar}
                              onChange={(e) => updateCustomField(cf.key, { [`fieldOptions_${loc}`]: e.target.value, ...(loc === 'en' ? { fieldOptions: e.target.value } : {}) })}
                              rows={3}
                              dir={loc === 'ar' ? 'rtl' : undefined}
                              placeholder={`${t.dropdownOptionsHint} (${loc.toUpperCase()})`}
                              className="flex-1 rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] transition-colors resize-y"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2 border-t border-[#F1F1EF]">
        <SaveButton t={t} saving={saving} />
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Setup Component                                               */
/* ------------------------------------------------------------------ */

export default function EventSetup(props: SetupProps) {
  const {
    event,
    activeSection,
    showRestricted,
    fonts,
    speakers,
    partners,
    externalLinks,
    socialLink,
    venue,
    agenda,
    eventFiles,
    badges,
    urls,
    locale,
    logoUrl,
    accountName,
    userEmail,
    canEditAccount,
    translations: t,
  } = props

  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.condition || item.condition(props))

  function sectionHref(item: NavItem) {
    return item.param ? `${urls.setup}?${item.param}` : urls.setup
  }

  function renderSection() {
    switch (activeSection) {
      case 'event':
        return <MainSettingsSection event={event} urls={urls} t={t} showRestricted={showRestricted} />
      case 'options':
        return <OptionsSection event={event} urls={urls} t={t} />
      case 'messages':
        return <MessagesSection event={event} urls={urls} t={t} />
      case 'font':
        return <FontSection event={event} fonts={fonts} urls={urls} t={t} />
      case 'template':
        return <TemplateSection event={event} urls={urls} t={t} />
      case 'badge':
        return <BadgeSection event={event} badges={badges} badgeAvailableFields={props.badgeAvailableFields} urls={urls} t={t} />
      case 'event_images':
        return <EventImagesSection event={event} urls={urls} t={t} />
      case 'speaker':
        return <SpeakersSection speakers={speakers} eventId={event.id} urls={urls} t={t} />
      case 'partner':
        return <PartnersSection partners={partners} eventId={event.id} urls={urls} t={t} />
      case 'external_link':
        return <ExternalLinksSection externalLinks={externalLinks} eventId={event.id} urls={urls} t={t} />
      case 'social_link':
        return <SocialLinksSection socialLink={socialLink} eventId={event.id} urls={urls} t={t} />
      case 'venue':
        return <VenueSection venue={venue} eventId={event.id} urls={urls} t={t} />
      case 'agenda':
        return <AgendaSection agenda={agenda} eventId={event.id} urls={urls} t={t} groupSessions={event.settings?.registration?.agenda_group_sessions !== false} />
      case 'event_file':
        return <EventFilesSection eventFiles={eventFiles} eventId={event.id} urls={urls} t={t} />
      case 'registration':
        return <RegistrationSection event={event} registrationFields={props.registrationFields} customFields={props.customFields} urls={urls} t={t} />
      case 'registration_rules':
        return <RegistrationRulesSection event={event} urls={urls} t={t} />
      case 'registration_links':
        return <RegistrationLinksSection links={props.registrationLinks} options={props.registrationLinkOptions} urls={urls} t={t} />
      case 'registration_design':
        return <RegistrationDesignSection event={event} urls={urls} t={t} />
      default:
        return <MainSettingsSection event={event} urls={urls} t={t} showRestricted={showRestricted} />
    }
  }

  const activeSectionLabel = t[visibleNavItems.find((i) => i.key === activeSection)?.labelKey || 'mainSettings'] || t.mainSettings

  return (
    <>
      <Head title={`${event.name} — ${t.eventSetup}`} />

      <AppShell
        locale={locale}
        logoUrl={logoUrl}
        accountName={accountName}
        userEmail={userEmail}
        canEditAccount={canEditAccount}
        urls={{
          events: urls.events,
          accountSettings: urls.accountSettings,
          changePassword: urls.changePassword,
          logout: urls.logout,
          whatsappTemplates: urls.whatsappTemplates,
          invitations: urls.invitations,
        }}
        translations={{
          events: t.events,
          accountSettings: t.accountSettings,
          changePassword: t.changePassword,
          logout: t.logout,
          logoutConfirm: t.logoutConfirm,
          logoutConfirmBody: t.logoutConfirmBody,
          cancel: t.cancel,
          whatsappTemplates: t.whatsappTemplates,
          invitations: t.invitations,
        }}
      >
        {/* ─── Back link ─── */}
        <div className="pt-4 pb-1">
          <a
            href={urls.dashboard}
            className="inline-flex items-center gap-1.5 text-sm text-[#91918E] hover:text-[#37352F] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.backToDashboard}
          </a>
        </div>

        {/* ─── Page header ─── */}
        <div className="pb-4">
          <div className="flex items-center gap-2">
            <div className="text-3xl">⚙️</div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-[#37352F] tracking-tight truncate">
                {event.name}
              </h1>
              <p className="text-sm text-[#91918E]">{t.eventSetup}</p>
            </div>
          </div>
        </div>

        {/* ─── Mobile nav toggle ─── */}
        <div className="sm:hidden mb-3">
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="flex items-center gap-2 rounded-md border border-[#E8E8E5] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF] transition-colors w-full cursor-pointer"
          >
            {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            {activeSectionLabel}
          </button>
        </div>

        {/* ─── Two-column layout ─── */}
        <div className="flex gap-6 pb-12">
          {/* Sidebar */}
          <nav className={`shrink-0 w-52 ${mobileNavOpen ? 'block' : 'hidden'} sm:block`}>
            <div className="sticky top-14 space-y-0.5">
              {visibleNavItems.map((item) => {
                const Icon = item.icon
                const isActive = item.key === activeSection
                return (
                  <Link
                    key={item.key}
                    href={sectionHref(item)}
                    preserveScroll
                    className={`flex items-center gap-2 rounded-[4px] px-2.5 py-1.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-[#F1F1EF] text-[#37352F] font-medium'
                        : 'text-[#91918E] hover:bg-[#F7F7F5] hover:text-[#37352F]'
                    }`}
                    onClick={() => setMobileNavOpen(false)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{t[item.labelKey] || item.labelKey}</span>
                  </Link>
                )
              })}
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-[#37352F]">{activeSectionLabel}</h2>
            </div>
            {renderSection()}
          </div>
        </div>
      </AppShell>
    </>
  )
}
